import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getApiErrorMessage } from '../utils/apiError';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const STORAGE_KEYS = {
  access: 'authToken',
  refresh: 'refreshToken',
  authed: 'isAuthenticated',
} as const;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 120_000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_KEYS.access);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  try {
    const raw = localStorage.getItem('creditnow-tenant-context');
    if (raw) {
      const parsed = JSON.parse(raw);
      const state = parsed?.state ?? parsed;
      if (state?.legalEntityId) {
        config.headers['X-Legal-Entity-Id'] = state.legalEntityId;
      }
      if (state?.branchId) {
        config.headers['X-Branch-Id'] = state.branchId;
      }
    }
  } catch {
    /* ignore */
  }
  // FormData necesita boundary del browser; el default application/json lo rompe.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers && typeof (config.headers as any).delete === 'function') {
      (config.headers as any).delete('Content-Type');
    } else if (config.headers) {
      delete (config.headers as any)['Content-Type'];
    }
  }
  return config;
});

// --- Refresh token rotation ---
// Si llega 401 a una petición autenticada, llamamos /auth/refresh con el refresh
// guardado. Si lo logramos, reintentamos la petición original con el nuevo access.
// Si falla (o no hay refresh), borramos el storage y mandamos a /login.

let refreshing: Promise<string | null> | null = null;
async function tryRefresh(): Promise<string | null> {
  const refresh = localStorage.getItem(STORAGE_KEYS.refresh);
  if (!refresh) return null;
  try {
    const { data } = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, { refresh_token: refresh });
    if (data?.access_token) localStorage.setItem(STORAGE_KEYS.access, data.access_token);
    if (data?.refresh_token) localStorage.setItem(STORAGE_KEYS.refresh, data.refresh_token);
    return data?.access_token ?? null;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retried?: boolean };
    const isAuthEndpoint = original?.url?.includes('/auth/refresh') || original?.url?.includes('/auth/login');

    if (error.response?.status === 401 && original && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      if (!refreshing) refreshing = tryRefresh();
      const newToken = await refreshing;
      refreshing = null;

      if (newToken) {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` } as any;
        return api(original);
      }

      localStorage.removeItem(STORAGE_KEYS.access);
      localStorage.removeItem(STORAGE_KEYS.refresh);
      localStorage.removeItem(STORAGE_KEYS.authed);
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Normaliza 502/503/504 y HTML de nginx a un mensaje usable en UI.
    // Con responseType:'blob', el body de error Nest viene como Blob: parsearlo a JSON.
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text) as { message?: string | string[]; error?: string };
        (error.response as any).data = parsed;
      } catch {
        // deja el Blob; getApiErrorMessage caerá al status genérico
      }
    }
    const friendly = getApiErrorMessage(error);
    (error as AxiosError & { friendlyMessage?: string }).friendlyMessage = friendly;
    error.message = friendly;
    if (error.response) {
      const prev = error.response.data;
      if (prev && typeof prev === 'object' && !Array.isArray(prev) && !(prev instanceof Blob)) {
        (error.response as any).data = { ...prev, message: friendly };
      } else {
        (error.response as any).data = { message: friendly };
      }
    }

    return Promise.reject(error);
  },
);

// Interfaces para las entidades
export interface Customer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  dateOfBirth: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER';
  maritalStatus: 'SINGLE' | 'MARRIED' | 'DIVORCED' | 'WIDOWED';
  nationality: string;
  documentType: string;
  documentNumber: string;
  kycStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  employmentStatus: 'EMPLOYED' | 'UNEMPLOYED' | 'SELF_EMPLOYED' | 'RETIRED';
  income: number;
  creditScore: number;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  createdAt: string;
  updatedAt: string;
}

export interface Loan {
  id: string;
  customerId: string;
  amount: number;
  interestRate: number;
  termMonths: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'ACTIVE' | 'COMPLETED' | 'DEFAULTED';
  disbursementDate?: string;
  maturityDate: string;
  monthlyPayment: number;
  totalAmount: number;
  remainingBalance: number;
  customer?: Customer;
  installments?: LoanInstallment[];
  createdAt: string;
  updatedAt: string;
}

export interface LoanInstallment {
  id: string;
  loanId: string;
  installmentNumber: number;
  dueDate: string;
  amount: number;
  principalAmount: number;
  interestAmount: number;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'DEFAULTED';
  paidDate?: string;
  paidAmount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Payment {
  id: string;
  loanId: string;
  installmentId?: string;
  amount: number;
  paymentMethod: 'CASH' | 'BANK_TRANSFER' | 'CREDIT_CARD' | 'DEBIT_CARD';
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
  transactionId?: string;
  processedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Collection {
  id: string;
  loanId: string;
  customerId: string;
  amount: number;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  method: 'PHONE' | 'EMAIL' | 'SMS' | 'LETTER' | 'LEGAL';
  notes?: string;
  assignedTo?: string;
  dueDate: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface KYC {
  id: string;
  customerId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'REQUIRES_REVIEW';
  documentType: string;
  documentNumber: string;
  documentImage?: string;
  documentBackImage?: string;
  selfieImage?: string;
  verificationScore: number;
  rejectionReason?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RiskAssessment {
  id: string;
  customerId: string;
  loanId?: string;
  riskScore: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  factors: string[];
  recommendations: string[];
  approved: boolean;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  isRead: boolean;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardStats {
  totalCustomers: number;
  activeLoans: number;
  totalLoanAmount: number;
  monthlyRevenue: number;
  pendingKYC: number;
  overduePayments: number;
  riskAlerts: number;
  recentActivity: any[];
}

export interface AnalyticsData {
  period: { from: string; to: string };
  totalCustomers: number;
  totalLoans: number;
  totalAmount: number;
  averageLoanSize: number;
  approvalRate: number;
  defaultRate: number;
  revenue: number;
  expenses: number;
  profit: number;
  totalPayments: number;
  totalCollected: number;
  pendingCollections: number;
  monthlyTrend: Array<{
    label: string;
    key: string;
    loans: number;
    payments: number;
    collected: number;
  }>;
}

// Servicios de API
export class ApiService {
  // Auth
  async login(email: string, password: string) {
    const response = await api.post('/api/v1/auth/login', { email, password });
    if (response.data.access_token) {
      localStorage.setItem(STORAGE_KEYS.access, response.data.access_token);
      localStorage.setItem(STORAGE_KEYS.authed, 'true');
    }
    if (response.data.refresh_token) {
      localStorage.setItem(STORAGE_KEYS.refresh, response.data.refresh_token);
    }
    return response.data;
  }

  async logout() {
    const refresh = localStorage.getItem(STORAGE_KEYS.refresh);

    // Limpiar primero: si el redirect corta la petición, igual quedamos deslogueados.
    localStorage.removeItem(STORAGE_KEYS.access);
    localStorage.removeItem(STORAGE_KEYS.refresh);
    localStorage.removeItem(STORAGE_KEYS.authed);
    localStorage.removeItem('cashSessionId');
    localStorage.removeItem('creditnow-tenant-context');
    sessionStorage.removeItem('sessionPermissions');

    if (refresh) {
      try {
        await api.post(
          '/api/v1/auth/logout',
          { refresh_token: refresh },
          { validateStatus: (status) => status === 204 || (status >= 200 && status < 300) },
        );
      } catch {
        // best-effort: el estado local ya se limpió
      }
    }
  }

  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await api.get('/api/v1/monitoring/stats');
    return response.data;
  }

  async getOperationalDashboard(params?: {
    year?: number;
    month?: number;
    branchId?: string;
    from?: string;
    to?: string;
    preset?: string;
    compare?: string;
    sections?: string;
  }): Promise<any> {
    const response = await api.get('/api/v1/reports/operational-dashboard', { params });
    return response.data;
  }

  async getReportsDashboardStats(): Promise<{
    totalCustomers: number;
    totalLoans: number;
    activeLoans: number;
    totalPayments: number;
    pendingCollections: number;
  }> {
    const response = await api.get('/api/v1/reports/dashboard');
    return response.data;
  }

  async getAnalytics(params: { from: string; to: string }): Promise<AnalyticsData> {
    const response = await api.get('/api/v1/reports/analytics', { params });
    return response.data;
  }

  async downloadReportCsv(
    report: 'loans' | 'payments' | 'collections' | 'accounting-balance',
    options?: { legalEntityId?: string },
  ): Promise<void> {
    const path = report === 'accounting-balance'
      ? '/api/v1/reports/accounting-balance.csv'
      : `/api/v1/reports/${report}.csv`;
    const params =
      report === 'accounting-balance' && options?.legalEntityId
        ? { legalEntityId: options.legalEntityId }
        : undefined;
    const response = await api.get(path, { responseType: 'blob', params });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Audit
  async getAuditLogs(params?: {
    actorId?: string;
    entity?: string;
    action?: string;
    operation?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ logs: any[]; total: number; data?: any[] }> {
    const response = await api.get('/api/v1/audit/logs', { params });
    return response.data;
  }

  async getAuditLogById(id: string): Promise<any> {
    const response = await api.get(`/api/v1/audit/logs/${id}`);
    return response.data?.data ?? response.data;
  }

  async getAuditMetrics(): Promise<any> {
    const response = await api.get('/api/v1/audit/metrics');
    return response.data;
  }

  // Customers
  async listCustomers(params?: {
    page?: number;
    pageSize?: number;
    q?: string;
    branchId?: string;
    status?: string;
  }): Promise<{ data: any[]; meta: { total: number; page: number; pageSize: number; pageCount: number } }> {
    const response = await api.get('/api/v1/customers', { params });
    const body = response.data?.data && response.data?.meta ? response.data : response.data;
    if (Array.isArray(body)) {
      return {
        data: body,
        meta: { total: body.length, page: 1, pageSize: body.length || 25, pageCount: 1 },
      };
    }
    return {
      data: body?.data ?? [],
      meta: body?.meta ?? { total: 0, page: 1, pageSize: 25, pageCount: 1 },
    };
  }

  /** Compat: primeras páginas para combos (máx 100). Preferí listCustomers en listados. */
  async getCustomers(params?: { q?: string; branchId?: string }): Promise<any[]> {
    const { data } = await this.listCustomers({ page: 1, pageSize: 100, ...params });
    return data;
  }

  async getCustomer(id: string): Promise<any> {
    const response = await api.get(`/api/v1/customers/${id}`);
    return response.data;
  }

  async createCustomer(data: {
    firstName: string;
    lastName: string;
    email: string;
    birthDate: string;
    gender: 'M' | 'F' | 'X';
    phone?: string;
    monthlyIncome?: number;
    employmentStatus?: string;
    documentNumber?: string;
    branchId?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/customers', data);
    return response.data;
  }

  async updateCustomer(id: string, data: any): Promise<any> {
    const response = await api.patch(`/api/v1/customers/${id}`, data);
    return response.data;
  }

  async deleteCustomer(id: string): Promise<void> {
    await api.delete(`/api/v1/customers/${id}`);
  }

  // Loans
  async listLoans(params?: {
    page?: number;
    pageSize?: number;
    q?: string;
    branchId?: string;
    status?: string;
    excludePending?: boolean;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ data: any[]; meta: { total: number; page: number; pageSize: number; pageCount: number } }> {
    const response = await api.get('/api/v1/loans', { params });
    const body = response.data?.data && response.data?.meta ? response.data : response.data;
    if (Array.isArray(body)) {
      return {
        data: body,
        meta: { total: body.length, page: 1, pageSize: body.length || 25, pageCount: 1 },
      };
    }
    return {
      data: body?.data ?? [],
      meta: body?.meta ?? { total: 0, page: 1, pageSize: 25, pageCount: 1 },
    };
  }

  /** Compat: primeras páginas para combos (máx 100). Preferí listLoans en listados. */
  async getLoans(params?: {
    q?: string;
    branchId?: string;
    status?: string;
    excludePending?: boolean;
  }): Promise<any[]> {
    const { data } = await this.listLoans({ page: 1, pageSize: 100, ...params });
    return data;
  }

  async getLoan(id: string): Promise<any> {
    const response = await api.get(`/api/v1/loans/${id}`);
    return response.data;
  }

  /** Blob del comprobante de transferencia de desembolso (PDF/imagen). */
  async getLoanTransferProofBlob(loanId: string): Promise<Blob> {
    const response = await api.get(`/api/v1/loans/${loanId}/transfer-proof`, {
      responseType: 'blob',
    });
    const contentType =
      (response.headers?.['content-type'] as string) ||
      (response.data instanceof Blob ? response.data.type : '') ||
      'application/octet-stream';
    if (response.data instanceof Blob && response.data.type) {
      return response.data;
    }
    return new Blob([response.data], { type: contentType });
  }

  /** Re-sube comprobante (GCS) sin re-desembolsar. */
  async replaceLoanTransferProof(loanId: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/api/v1/loans/${loanId}/transfer-proof`, form);
    return response.data;
  }

  async createLoan(data: {
    customerId: string;
    productId: string;
    amount: number;
    termMonths: number;
    interestRate?: number;
    startDate?: string;
    validationOverride?: boolean;
    validationOverrideReason?: string;
    channelType?: 'PRIVATE' | 'PUBLIC';
    publicEntityId?: string;
    employeeFileNumber?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/loans', data);
    return response.data;
  }

  async approveLoan(
    id: string,
    override?: {
      validationOverride?: boolean;
      validationOverrideReason?: string;
      channel?: 'CASH' | 'BANK';
      cashRegisterId?: string;
      cashSessionId?: string;
      bankAccountId?: string;
      reference?: string;
    },
  ): Promise<any> {
    const response = await api.post(`/api/v1/loans/${id}/approve`, override ?? {});
    return response.data;
  }

  async completeBankDisbursement(
    id: string,
    data: { bankAccountId: string; reference?: string; file: File },
  ): Promise<any> {
    const form = new FormData();
    form.append('file', data.file);
    form.append('bankAccountId', data.bankAccountId);
    if (data.reference) form.append('reference', data.reference);
    const response = await api.post(`/api/v1/loans/${id}/complete-bank-disbursement`, form);
    return response.data;
  }

  async listDisbursementBankAccounts(opts: {
    branchId?: string;
    legalEntityId?: string;
  } = {}): Promise<any[]> {
    const response = await api.get('/api/v1/loans/disbursement-bank-accounts', {
      params: {
        ...(opts.branchId ? { branchId: opts.branchId } : {}),
        ...(opts.legalEntityId ? { legalEntityId: opts.legalEntityId } : {}),
      },
    });
    return response.data;
  }

  async rejectLoan(id: string): Promise<any> {
    const response = await api.post(`/api/v1/loans/${id}/reject`);
    return response.data;
  }

  async cancelLoan(id: string, data: { reason: string; cashSessionId?: string }): Promise<any> {
    const response = await api.post(`/api/v1/loans/${id}/cancel`, data);
    return response.data;
  }

  async renewLoan(
    parentLoanId: string,
    data: {
      renewalType: 'RENEWAL' | 'REENTRY' | 'REFINANCE';
      amount: number;
      termMonths: number;
      productId?: string;
      interestRate?: number;
      branchId?: string;
    },
  ): Promise<any> {
    const response = await api.post(`/api/v1/loans/${parentLoanId}/renew`, data);
    return response.data;
  }

  async listLoanRenewals(branchId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/loans/renewals', {
      params: branchId ? { branchId } : undefined,
    });
    return response.data;
  }

  // Loan applications (presolicitudes)
  async listLoanApplications(status?: string): Promise<any[]> {
    const response = await api.get('/api/v1/loan-applications', {
      params: status ? { status } : undefined,
    });
    return response.data;
  }

  async getLoanApplication(id: string): Promise<any> {
    const response = await api.get(`/api/v1/loan-applications/${id}`);
    return response.data;
  }

  async lookupLoanApplicationPerson(q: string): Promise<any> {
    const response = await api.get('/api/v1/loan-applications/lookup', { params: { q } });
    return response.data;
  }

  async ensureLoanApplicationCustomer(data: {
    query: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
    branchId?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/loan-applications/ensure-customer', data);
    return response.data;
  }

  async createLoanApplication(data: {
    documentNumber: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    requestedAmount: number;
    requestedTermMonths?: number;
    /** YYYY-MM-DD fecha inicio/otorgamiento */
    startDate?: string;
    branchId?: string;
    referralSourceId?: string;
    productId?: string;
    notes?: string;
    validationOverride?: boolean;
    validationOverrideReason?: string;
    decision?: 'CONTINUE' | 'BLOCK';
    channelType?: 'PRIVATE' | 'PUBLIC';
    publicEntityId?: string;
    employeeFileNumber?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/loan-applications', data);
    return response.data;
  }

  async uploadOriginationDocument(
    customerId: string,
    kind: 'PAYSLIP' | 'DNI' | 'DNI_FRONT' | 'DNI_BACK',
    file: File,
  ): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/api/v1/loan-applications/documents', form, {
      params: { customerId, kind },
    });
    return response.data;
  }

  async confirmOriginationDocument(
    id: string,
    data: {
      confirmedNet?: number;
      confirmedPeriod?: string;
      confirmedPayday?: string;
      confirmedDeductions?: string;
      confirmedDniNumber?: string;
      confirmedFirstName?: string;
      confirmedLastName?: string;
      confirmedExpiry?: string;
    },
  ): Promise<any> {
    const response = await api.patch(`/api/v1/loan-applications/documents/${id}`, data);
    return response.data;
  }

  async extractOriginationDocument(id: string): Promise<any> {
    const response = await api.post(`/api/v1/loan-applications/documents/${id}/extract`);
    return response.data;
  }

  async listOriginationDocuments(customerId: string): Promise<any[]> {
    const response = await api.get('/api/v1/loan-applications/documents', {
      params: { customerId },
    });
    return response.data;
  }

  async getOriginationDocumentFileBlob(id: string): Promise<Blob> {
    const response = await api.get(`/api/v1/loan-applications/documents/${id}/file`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getOriginationOcrStatus(): Promise<{ enabled: boolean; provider: string; model: string }> {
    const response = await api.get('/api/v1/loan-applications/ocr-status');
    return response.data;
  }

  async reviewLoanApplication(
    id: string,
    data: { status: string; notes?: string; validationOverride?: boolean; validationOverrideReason?: string },
  ): Promise<any> {
    const response = await api.patch(`/api/v1/loan-applications/${id}/review`, data);
    return response.data;
  }

  async convertLoanApplication(id: string, data: Record<string, unknown> = {}): Promise<any> {
    const response = await api.post(`/api/v1/loan-applications/${id}/convert`, data);
    return response.data;
  }

  async listReferralSources(): Promise<any[]> {
    const response = await api.get('/api/v1/loan-applications/referral-sources');
    return response.data;
  }

  // Customer compliance
  async checkCustomerBlock(documentNumber: string): Promise<{ blocked: boolean; reason?: string }> {
    const response = await api.get(`/api/v1/customers/compliance/blocks/check/${documentNumber}`);
    return response.data;
  }

  async listCustomerBlocks(): Promise<any[]> {
    const response = await api.get('/api/v1/customers/compliance/blocks');
    return response.data;
  }

  async createCustomerBlock(data: {
    documentNumber: string;
    reason: string;
    customerId?: string;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/customers/compliance/blocks', data);
    return response.data;
  }

  async liftCustomerBlock(blockId: string): Promise<any> {
    const response = await api.post(`/api/v1/customers/compliance/blocks/${blockId}/lift`);
    return response.data;
  }

  async queryBureau(documentNumber: string, provider?: string): Promise<any> {
    const response = await api.post('/api/v1/customers/compliance/bureau/query', {
      documentNumber,
      provider,
    });
    return response.data;
  }

  async listBureauQueries(documentNumber?: string): Promise<any[]> {
    const response = await api.get('/api/v1/customers/compliance/bureau/queries', {
      params: documentNumber ? { documentNumber } : undefined,
    });
    return response.data;
  }

  async listDiscountCodes(activeOnly = false): Promise<any[]> {
    const response = await api.get('/api/v1/discount-codes', {
      params: { activeOnly: activeOnly ? undefined : 'false' },
    });
    return response.data;
  }

  async createDiscountCode(data: {
    code: string;
    description?: string;
    percentOff: number;
    isActive?: boolean;
  }): Promise<any> {
    const response = await api.post('/api/v1/discount-codes', data);
    return response.data;
  }

  async setDiscountCodeActive(id: string, isActive: boolean): Promise<any> {
    const response = await api.patch(`/api/v1/discount-codes/${id}/active`, { isActive });
    return response.data;
  }

  async getLegalDaysThreshold(): Promise<{ days: number }> {
    const response = await api.get('/api/v1/settings/legal-days-threshold');
    return response.data;
  }

  async updateLegalDaysThreshold(days: number): Promise<{ days: number }> {
    const response = await api.patch('/api/v1/settings/legal-days-threshold', { days });
    return response.data;
  }

  async listLegalCandidates(): Promise<any[]> {
    const response = await api.get('/api/v1/collections/legal/candidates');
    return response.data;
  }

  async listLegalLoans(): Promise<any[]> {
    const response = await api.get('/api/v1/collections/legal/loans');
    return response.data;
  }

  async escalateToLegal(loanId: string): Promise<any> {
    const response = await api.post(`/api/v1/collections/legal/escalate/${loanId}`);
    return response.data;
  }

  async runLegalEscalation(): Promise<{ escalated: number; skipped: number }> {
    const response = await api.post('/api/v1/collections/legal/run');
    return response.data;
  }

  async quoteEarlyPayoff(loanId: string): Promise<any> {
    const response = await api.get(`/api/v1/loans/${loanId}/early-payoff-quote`);
    return response.data;
  }

  async earlyPayoff(loanId: string, quoteId: string): Promise<any> {
    const response = await api.post(`/api/v1/loans/${loanId}/early-payoff`, { quoteId });
    return response.data;
  }

  async reverseEarlyPayoff(loanId: string, data?: { reason?: string }): Promise<any> {
    const response = await api.post(`/api/v1/loans/${loanId}/reverse-early-payoff`, data ?? {});
    return response.data;
  }

  /** 4C — read-model financiero canónico (preferido sobre financial-summary). */
  async getLoanFinancialSnapshot(
    loanId: string,
    params?: { asOf?: string },
  ): Promise<import('../pages/loans/loan-financial.types').LoanFinancialSnapshotV1> {
    const response = await api.get(`/api/v1/loans/${loanId}/financial-snapshot`, { params });
    return response.data;
  }

  /** 4H — conciliación READ-ONLY. */
  async getLoanReconciliation(
    loanId: string,
    params?: { mode?: 'fast' | 'full' | 'FAST' | 'FULL'; asOf?: string },
  ): Promise<import('../pages/loans/loan-financial.types').LoanReconciliationResult> {
    const response = await api.get(`/api/v1/loans/${loanId}/reconciliation`, {
      params: {
        mode: params?.mode ?? 'fast',
        asOf: params?.asOf,
      },
    });
    return response.data;
  }

  async getLoanEffectivePolicy(
    loanId: string,
  ): Promise<import('../pages/loans/loan-financial.types').LoanEffectivePolicy> {
    const response = await api.get(`/api/v1/loans/${loanId}/effective-policy`);
    return response.data;
  }

  /** 4D — cotización payoff (POST; crea quote ACTIVE). */
  async createPayoffQuote(
    loanId: string,
    params?: { asOf?: string },
  ): Promise<import('../pages/loans/loan-financial.types').PayoffQuote> {
    const response = await api.post(`/api/v1/loans/${loanId}/payoff/quote`, null, { params });
    return response.data;
  }

  async confirmPayoffQuote(
    loanId: string,
    data: {
      quoteId: string;
      cashRegisterId?: string;
      cashSessionId?: string;
      bankAccountId?: string;
      paymentMethod?: string;
    },
  ): Promise<any> {
    const response = await api.post(`/api/v1/loans/${loanId}/payoff/confirm`, data);
    return response.data;
  }

  /** 4K — cotización refinanciación. */
  async createRestructuringQuote(
    loanId: string,
    data: {
      productId: string;
      termMonths: number;
      interestRate?: number;
      startDate?: string;
      branchId?: string;
      asOf?: string;
    },
  ): Promise<any> {
    const response = await api.post(`/api/v1/loans/${loanId}/restructuring/quote`, data);
    return response.data;
  }

  async confirmRestructuringQuote(
    loanId: string,
    data: { quoteId: string },
  ): Promise<any> {
    const response = await api.post(`/api/v1/loans/${loanId}/restructuring/confirm`, data);
    return response.data;
  }

  async getLoanCreditRisk(loanId: string): Promise<any> {
    const response = await api.get(`/api/v1/loans/${loanId}/credit-risk`);
    return response.data;
  }

  async previewLoanProvision(loanId: string, params?: { asOf?: string }): Promise<any> {
    const response = await api.get(`/api/v1/loans/${loanId}/provisions/preview`, { params });
    return response.data;
  }

  async postLoanProvision(loanId: string, data?: { asOf?: string }): Promise<any> {
    const response = await api.post(`/api/v1/loans/${loanId}/provisions`, data ?? {});
    return response.data;
  }

  async previewLoanWriteOff(loanId: string, params?: { asOf?: string }): Promise<any> {
    const response = await api.get(`/api/v1/loans/${loanId}/write-offs/preview`, { params });
    return response.data;
  }

  async postLoanWriteOff(loanId: string, data: { reason: string; asOf?: string }): Promise<any> {
    const response = await api.post(`/api/v1/loans/${loanId}/write-offs`, data);
    return response.data;
  }

  async postLoanRecovery(
    loanId: string,
    data: {
      amount: number;
      asOf?: string;
      paymentMethod?: string;
      cashRegisterId?: string;
      bankAccountId?: string;
      reference?: string;
      idempotencyKey?: string;
    },
  ): Promise<any> {
    const response = await api.post(`/api/v1/loans/${loanId}/recoveries`, data);
    return response.data;
  }

  async getAuditLogsByEntity(entity: string, entityId: string): Promise<any> {
    const response = await api.get(`/api/v1/audit/logs/entity/${entity}/${entityId}`);
    return response.data;
  }

  async listHolidays(): Promise<any[]> {
    const response = await api.get('/api/v1/reference-data/holidays', { params: { activeOnly: 'false' } });
    return response.data;
  }

  async createHoliday(data: { date: string; name: string }): Promise<any> {
    const response = await api.post('/api/v1/reference-data/holidays', data);
    return response.data;
  }

  async deactivateHoliday(id: string): Promise<any> {
    const response = await api.patch(`/api/v1/reference-data/holidays/${id}/deactivate`);
    return response.data;
  }

  async listLocalities(): Promise<any[]> {
    const response = await api.get('/api/v1/reference-data/localities', { params: { activeOnly: 'false' } });
    return response.data;
  }

  async createLocality(data: { name: string; province?: string; postalCode?: string }): Promise<any> {
    const response = await api.post('/api/v1/reference-data/localities', data);
    return response.data;
  }

  async deactivateLocality(id: string): Promise<any> {
    const response = await api.patch(`/api/v1/reference-data/localities/${id}/deactivate`);
    return response.data;
  }

  async listCashConceptsRef(): Promise<any[]> {
    const response = await api.get('/api/v1/reference-data/cash-concepts', { params: { activeOnly: 'false' } });
    return response.data;
  }

  async createCashConceptRef(data: { code: string; name: string; description?: string }): Promise<any> {
    const response = await api.post('/api/v1/reference-data/cash-concepts', data);
    return response.data;
  }

  async updateCashConceptRef(id: string, data: { name?: string; description?: string; isActive?: boolean }): Promise<any> {
    const response = await api.patch(`/api/v1/reference-data/cash-concepts/${id}`, data);
    return response.data;
  }

  async listReferralSourcesRef(): Promise<any[]> {
    const response = await api.get('/api/v1/reference-data/referral-sources', { params: { activeOnly: 'false' } });
    return response.data;
  }

  async createReferralSourceRef(data: { code: string; name: string }): Promise<any> {
    const response = await api.post('/api/v1/reference-data/referral-sources', data);
    return response.data;
  }

  async updateReferralSourceRef(id: string, data: { name?: string; isActive?: boolean }): Promise<any> {
    const response = await api.patch(`/api/v1/reference-data/referral-sources/${id}`, data);
    return response.data;
  }

  async listOccupationsRef(): Promise<any[]> {
    const response = await api.get('/api/v1/reference-data/occupations', { params: { activeOnly: 'false' } });
    return response.data;
  }

  async createOccupationRef(data: { code: string; name: string }): Promise<any> {
    const response = await api.post('/api/v1/reference-data/occupations', data);
    return response.data;
  }

  async updateOccupationRef(id: string, data: { name?: string; isActive?: boolean }): Promise<any> {
    const response = await api.patch(`/api/v1/reference-data/occupations/${id}`, data);
    return response.data;
  }

  async listBureauProvidersRef(): Promise<any[]> {
    const response = await api.get('/api/v1/reference-data/bureau-providers');
    return response.data;
  }

  async listPublicEntities(activeOnly = true, branchId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/public-entities', {
      params: {
        activeOnly: activeOnly ? 'true' : 'false',
        ...(branchId ? { branchId } : {}),
      },
    });
    return response.data;
  }

  async listPayrollPublicEntities(branchId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/payroll-discounts/entities', {
      params: branchId ? { branchId } : undefined,
    });
    return response.data;
  }

  async publicEntityLayoutDefaults(): Promise<any> {
    const response = await api.get('/api/v1/public-entities/defaults');
    return response.data;
  }

  async createPublicEntity(data: {
    code: string;
    name: string;
    encoding?: string;
    sendLayout?: unknown;
    responseLayout?: unknown;
    sendFileConfig?: unknown;
    responseFileConfig?: unknown;
    notes?: string;
    branchIds: string[];
    primaryBranchId: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/public-entities', data);
    return response.data;
  }

  async updatePublicEntity(id: string, data: Record<string, unknown>): Promise<any> {
    const response = await api.patch(`/api/v1/public-entities/${id}`, data);
    return response.data;
  }

  async previewPayrollDiscounts(params: {
    publicEntityId: string;
    from: string;
    to: string;
  }): Promise<any> {
    const response = await api.get('/api/v1/payroll-discounts/preview', { params });
    return response.data;
  }

  async listPayrollPendingMovements(publicEntityId: string): Promise<any[]> {
    const response = await api.get('/api/v1/payroll-discounts/movements', {
      params: { publicEntityId, status: 'PENDING' },
    });
    return response.data;
  }

  async createPayrollDiscountMovements(data: {
    publicEntityId: string;
    from: string;
    to: string;
    installmentIds: string[];
  }): Promise<any> {
    const response = await api.post('/api/v1/payroll-discounts/movements', data);
    return response.data;
  }

  async generatePayrollDiscounts(data: {
    publicEntityId: string;
    fileDate: string;
    from?: string;
    to?: string;
    movementIds: string[];
  }): Promise<any> {
    const response = await api.post('/api/v1/payroll-discounts/generate', data);
    return response.data;
  }

  async listPayrollDiscountBatches(params?: { publicEntityId?: string; status?: string }): Promise<any[]> {
    const response = await api.get('/api/v1/payroll-discounts', { params });
    return response.data;
  }

  async getPayrollDiscountBatch(id: string): Promise<any> {
    const response = await api.get(`/api/v1/payroll-discounts/${id}`);
    return response.data?.data ?? response.data;
  }

  private async downloadPayrollBlob(url: string, fallbackName: string): Promise<void> {
    const response = await api.get(url, { responseType: 'blob' });
    const disposition = String(response.headers?.['content-disposition'] || '');
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    const filename = match?.[1] || fallbackName;
    const contentType = String(response.headers?.['content-type'] || 'application/octet-stream');
    const blob = new Blob([response.data], { type: contentType });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objectUrl);
  }

  async downloadPayrollDiscountFile(batchId: string): Promise<void> {
    await this.downloadPayrollBlob(
      `/api/v1/payroll-discounts/${batchId}/file`,
      `descuento-${batchId}.txt`,
    );
  }

  async downloadPayrollDiscountResponseFile(batchId: string): Promise<void> {
    await this.downloadPayrollBlob(
      `/api/v1/payroll-discounts/${batchId}/response-file`,
      `descuento-${batchId}-respuesta.txt`,
    );
  }

  async releasePayrollDiscounts(data: { publicEntityId?: string; fileDate?: string }): Promise<any> {
    const response = await api.post('/api/v1/payroll-discounts/release', data);
    return response.data;
  }

  async importPayrollDiscountResponse(
    batchId: string,
    content: string | string[],
  ): Promise<any> {
    const body = Array.isArray(content)
      ? { contents: content }
      : { content };
    const response = await api.post(`/api/v1/payroll-discounts/${batchId}/import`, body);
    return response.data;
  }

  async previewBaproDebit(params: {
    branchId: string;
    dueFrom: string;
    dueTo: string;
  }): Promise<any> {
    const response = await api.get('/api/v1/bapro-debit/preview', { params });
    return response.data;
  }

  async createBaproDebitBatch(data: {
    branchId: string;
    dueFrom: string;
    dueTo: string;
    installmentIds?: string[];
  }): Promise<any> {
    const response = await api.post('/api/v1/bapro-debit/batches', data);
    return response.data;
  }

  async sendBaproDebitBatch(id: string): Promise<any> {
    const response = await api.post(`/api/v1/bapro-debit/batches/${id}/send`);
    return response.data;
  }

  async listBaproDebitBatches(params?: {
    branchId?: string;
    status?: string;
    needsReview?: boolean;
  }): Promise<any> {
    const response = await api.get('/api/v1/bapro-debit/batches', {
      params: {
        ...params,
        needsReview: params?.needsReview ? 'true' : undefined,
      },
    });
    return response.data;
  }

  async getBaproDebitBatch(id: string): Promise<any> {
    const response = await api.get(`/api/v1/bapro-debit/batches/${id}`);
    return response.data;
  }

  async releaseBaproDebitBatch(
    id: string,
    body?: { reason?: string; rejectCode?: string },
  ): Promise<any> {
    const response = await api.post(`/api/v1/bapro-debit/batches/${id}/release`, body ?? {});
    return response.data;
  }

  async closeBaproDebitBatch(
    id: string,
    body?: { reason?: string; releaseOpen?: boolean },
  ): Promise<any> {
    const response = await api.post(`/api/v1/bapro-debit/batches/${id}/close`, body ?? {});
    return response.data;
  }

  async downloadBaproDebitFile(batchId: string, kind: 'excel' | 'fixed'): Promise<void> {
    await this.downloadPayrollBlob(
      `/api/v1/bapro-debit/batches/${batchId}/files/${kind}`,
      kind === 'excel' ? `EBT_${batchId}.xlsx` : `EBT_${batchId}.txt`,
    );
  }

  async getBaproActiveBatchForInstallment(installmentId: string): Promise<any> {
    const response = await api.get(`/api/v1/bapro-debit/installments/${installmentId}/active-batch`);
    return response.data;
  }

  /** 4J — import respuesta BAPRO (JSON normalizado { records: [...] }). */
  async importBaproDebitResponse(batchId: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/api/v1/bapro-debit/batches/${batchId}/import`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async importBaproDebitRecords(
    batchId: string,
    records: Array<Record<string, unknown>>,
  ): Promise<any> {
    const response = await api.post(`/api/v1/bapro-debit/batches/${batchId}/import-records`, {
      records,
    });
    return response.data;
  }

  async listPayrollBatchCollections(params?: {
    status?: string;
    publicEntityId?: string;
  }): Promise<any[]> {
    const response = await api.get('/api/v1/payroll-batch-collections', { params });
    return response.data?.data ?? response.data;
  }

  async getPayrollBatchCollection(id: string): Promise<any> {
    const response = await api.get(`/api/v1/payroll-batch-collections/${id}`);
    return response.data?.data ?? response.data;
  }

  async settlePayrollBatchCollection(
    id: string,
    body: { bankAccountId: string; amount: number; reference?: string; bankMovementId?: string },
  ): Promise<any> {
    const response = await api.post(`/api/v1/payroll-batch-collections/${id}/settle`, body);
    return response.data?.data ?? response.data;
  }

  async listValidationProviders(): Promise<any[]> {
    const response = await api.get('/api/v1/validation-providers');
    return response.data;
  }

  async listAdminPartners(): Promise<any[]> {
    const response = await api.get('/api/v1/admin/partners');
    return response.data?.data ?? response.data;
  }

  async getAdminPartner(id: string): Promise<any> {
    const response = await api.get(`/api/v1/admin/partners/${id}`);
    return response.data?.data ?? response.data;
  }

  async createAdminPartner(data: Record<string, unknown>): Promise<any> {
    const response = await api.post('/api/v1/admin/partners', data);
    return response.data?.data ?? response.data;
  }

  async updateAdminPartner(id: string, data: Record<string, unknown>): Promise<any> {
    const response = await api.patch(`/api/v1/admin/partners/${id}`, data);
    return response.data?.data ?? response.data;
  }

  async rotateAdminPartnerApiKey(id: string): Promise<any> {
    const response = await api.post(`/api/v1/admin/partners/${id}/rotate-api-key`);
    return response.data?.data ?? response.data;
  }

  async listAdminPartnerDeliveries(id: string, limit = 50): Promise<any[]> {
    const response = await api.get(`/api/v1/admin/partners/${id}/deliveries`, {
      params: { limit },
    });
    const body = response.data?.data ?? response.data;
    return Array.isArray(body) ? body : [];
  }

  async updateValidationProvider(id: string, data: Record<string, unknown>): Promise<any> {
    const response = await api.patch(`/api/v1/validation-providers/${id}`, data);
    return response.data;
  }

  async testValidationProvider(
    id: string,
    data?: { documentNumber?: string; taxId?: string },
  ): Promise<any> {
    const response = await api.post(`/api/v1/validation-providers/${id}/test`, data ?? {});
    return response.data;
  }

  async getCustomerValidations(customerId: string): Promise<any> {
    const response = await api.get(`/api/v1/customers/${customerId}/validations`);
    return response.data;
  }

  async runCustomerValidations(customerId: string, providerIds?: string[]): Promise<any> {
    const response = await api.post(`/api/v1/customers/${customerId}/validations/run`, {
      providerIds,
    });
    return response.data;
  }

  async recordManualValidation(
    customerId: string,
    providerId: string,
    data: { status: 'PASS' | 'FAIL'; summary: string },
  ): Promise<any> {
    const response = await api.post(
      `/api/v1/customers/${customerId}/validations/${providerId}/manual`,
      data,
    );
    return response.data;
  }

  async getApplicationValidations(applicationId: string): Promise<any> {
    const response = await api.get(`/api/v1/loan-applications/${applicationId}/validations`);
    return response.data;
  }

  async runApplicationValidations(applicationId: string, providerIds?: string[]): Promise<any> {
    const response = await api.post(`/api/v1/loan-applications/${applicationId}/validations/run`, {
      providerIds,
    });
    return response.data;
  }

  // Conciliación bancaria
  async listBankImportTemplates(): Promise<any[]> {
    const response = await api.get('/api/v1/bank-reconciliation/templates');
    return response.data;
  }

  async listBankAccounts(branchId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/bank-reconciliation/accounts', {
      params: branchId ? { branchId } : undefined,
    });
    return response.data;
  }

  async createBankAccount(data: {
    branchId: string;
    bankName: string;
    templateCode?: string;
    cbu?: string;
    alias?: string;
    accountNumber?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/bank-reconciliation/accounts', data);
    return response.data;
  }

  async updateBankAccount(
    id: string,
    data: {
      bankName?: string;
      templateCode?: string;
      cbu?: string | null;
      alias?: string | null;
      accountNumber?: string | null;
      isActive?: boolean;
      branchId?: string | null;
    },
  ): Promise<any> {
    const response = await api.patch(`/api/v1/bank-reconciliation/accounts/${id}`, data);
    return response.data;
  }

  async setBankAccountCashRegisters(accountId: string, registerIds: string[]): Promise<any[]> {
    const response = await api.put(`/api/v1/bank-reconciliation/accounts/${accountId}/cash-registers`, {
      registerIds,
    });
    return response.data;
  }

  async importBankStatement(accountId: string, file: File, templateCode?: string): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(
      `/api/v1/bank-reconciliation/accounts/${accountId}/import`,
      form,
      {
        params: templateCode ? { templateCode } : undefined,
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    return response.data;
  }

  async listBankMovements(
    accountId: string,
    params?: { from?: string; to?: string; status?: string },
  ): Promise<any[]> {
    const response = await api.get(`/api/v1/bank-reconciliation/accounts/${accountId}/movements`, { params });
    return response.data;
  }

  async getBankReconciliationSummary(
    accountId: string,
    params?: { from?: string; to?: string },
  ): Promise<any> {
    const response = await api.get(`/api/v1/bank-reconciliation/accounts/${accountId}/summary`, { params });
    return response.data;
  }

  async getBankReconciliationPeriod(accountId: string, periodKey: string): Promise<any> {
    const response = await api.get(
      `/api/v1/bank-reconciliation/accounts/${accountId}/periods/${periodKey}`,
    );
    return response.data;
  }

  async listBankUnmatchedSystem(accountId: string, periodKey: string): Promise<any> {
    const response = await api.get(
      `/api/v1/bank-reconciliation/accounts/${accountId}/periods/${periodKey}/unmatched-system`,
    );
    return response.data;
  }

  async completeBankReconciliationPeriod(
    accountId: string,
    periodKey: string,
    notes?: string,
  ): Promise<any> {
    const response = await api.post(
      `/api/v1/bank-reconciliation/accounts/${accountId}/periods/${periodKey}/complete`,
      { notes },
    );
    return response.data;
  }

  async reopenBankReconciliationPeriod(accountId: string, periodKey: string): Promise<any> {
    const response = await api.post(
      `/api/v1/bank-reconciliation/accounts/${accountId}/periods/${periodKey}/reopen`,
    );
    return response.data;
  }

  async autoMatchBankMovements(
    accountId: string,
    periodKey?: string,
  ): Promise<{ matched: number; skipped: number; processed: number; suggested?: number; ambiguous?: number }> {
    const response = await api.post(
      `/api/v1/bank-reconciliation/accounts/${accountId}/auto-match`,
      undefined,
      { params: periodKey ? { periodKey } : undefined },
    );
    return response.data;
  }

  async listBankMatchCandidates(accountId: string, movementId: string): Promise<any[]> {
    const response = await api.get(
      `/api/v1/bank-reconciliation/accounts/${accountId}/candidates/${movementId}`,
    );
    const data = response.data;
    return Array.isArray(data) ? data : data?.candidates ?? [];
  }

  async manualBankMatch(data: {
    bankMovementId: string;
    paymentMethodLineId?: string;
    paymentId?: string;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/bank-reconciliation/match', data);
    return response.data;
  }

  async unmatchBankMovement(bankMovementId: string): Promise<any> {
    const response = await api.delete(`/api/v1/bank-reconciliation/match/${bankMovementId}`);
    return response.data;
  }

  async ignoreBankMovement(movementId: string): Promise<any> {
    const response = await api.post(`/api/v1/bank-reconciliation/movements/${movementId}/ignore`);
    return response.data;
  }

  async createBankFeeFromMovement(
    movementId: string,
    data?: { description?: string; reference?: string },
  ): Promise<any> {
    const response = await api.post(
      `/api/v1/bank-reconciliation/movements/${movementId}/create-fee`,
      data ?? {},
    );
    return response.data;
  }

  // Payables / Fase 5
  async listSuppliers(legalEntityId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/payables/suppliers', {
      params: legalEntityId ? { legalEntityId } : undefined,
    });
    return response.data;
  }

  async createSupplier(data: {
    taxId: string;
    legalName: string;
    tradeName?: string;
    organizationId?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/payables/suppliers', data);
    return response.data;
  }

  async linkCompanySupplier(data: {
    legalEntityId: string;
    supplierId: string;
    paymentTermsDays?: number;
  }): Promise<any> {
    const response = await api.post('/api/v1/payables/suppliers/link', data);
    return response.data;
  }

  async listSupplierInvoices(legalEntityId: string, status?: string): Promise<any[]> {
    const response = await api.get('/api/v1/payables/invoices', {
      params: { legalEntityId, status },
    });
    return response.data;
  }

  async createSupplierInvoice(data: Record<string, unknown>): Promise<any> {
    const response = await api.post('/api/v1/payables/invoices', data);
    return response.data;
  }

  async registerSupplierInvoice(id: string): Promise<any> {
    const response = await api.post(`/api/v1/payables/invoices/${id}/register`);
    return response.data;
  }

  async listSupplierPayments(legalEntityId: string): Promise<any[]> {
    const response = await api.get('/api/v1/payables/payments', { params: { legalEntityId } });
    return response.data;
  }

  async listExpenses(legalEntityId: string): Promise<any[]> {
    const response = await api.get('/api/v1/payables/expenses', { params: { legalEntityId } });
    return response.data;
  }

  async createExpense(data: Record<string, unknown>): Promise<any> {
    const response = await api.post('/api/v1/payables/expenses', data);
    return response.data;
  }

  async postExpense(id: string): Promise<any> {
    const response = await api.post(`/api/v1/payables/expenses/${id}/post`);
    return response.data;
  }

  async listCostCenters(legalEntityId: string, includeInactive = false): Promise<any[]> {
    const response = await api.get('/api/v1/payables/cost-centers', {
      params: { legalEntityId, includeInactive: includeInactive ? 'true' : undefined },
    });
    return response.data;
  }

  async createCostCenter(data: {
    legalEntityId: string;
    code: string;
    name: string;
    description?: string;
    parentId?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/payables/cost-centers', data);
    return response.data;
  }

  async updateCostCenter(
    id: string,
    data: { name?: string; description?: string | null; isActive?: boolean },
  ): Promise<any> {
    const response = await api.patch(`/api/v1/payables/cost-centers/${id}`, data);
    return response.data;
  }

  async listBusinessUnits(legalEntityId: string, includeInactive = false): Promise<any[]> {
    const response = await api.get('/api/v1/accounting/business-units', {
      params: { legalEntityId, includeInactive: includeInactive ? 'true' : undefined },
    });
    return response.data;
  }

  async createBusinessUnit(data: {
    legalEntityId: string;
    code: string;
    name: string;
    description?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/accounting/business-units', data);
    return response.data;
  }

  async updateBusinessUnit(
    id: string,
    data: {
      legalEntityId?: string;
      name?: string;
      description?: string | null;
      isActive?: boolean;
    },
  ): Promise<any> {
    const response = await api.post(`/api/v1/accounting/business-units/${id}`, data);
    return response.data;
  }

  async deactivateBusinessUnit(id: string, legalEntityId?: string): Promise<any> {
    const response = await api.post(`/api/v1/accounting/business-units/${id}/deactivate`, null, {
      params: { legalEntityId },
    });
    return response.data;
  }

  async listInflationSeries(includeInactive = false): Promise<any[]> {
    const response = await api.get('/api/v1/accounting/inflation/index-series', {
      params: { includeInactive: includeInactive ? 'true' : undefined },
    });
    return response.data;
  }

  async createInflationSeries(data: {
    code: string;
    name: string;
    source?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/accounting/inflation/index-series', data);
    return response.data;
  }

  async upsertInflationValue(
    seriesId: string,
    data: { period: string; value: number | string },
  ): Promise<any> {
    const response = await api.post(
      `/api/v1/accounting/inflation/index-series/${seriesId}/values`,
      data,
    );
    return response.data;
  }

  async listInflationValues(seriesId: string): Promise<any[]> {
    const response = await api.get(
      `/api/v1/accounting/inflation/index-series/${seriesId}/values`,
    );
    return response.data;
  }

  async getInflationPolicy(legalEntityId: string): Promise<any> {
    const response = await api.get(
      `/api/v1/accounting/legal-entities/${legalEntityId}/inflation-policy`,
    );
    return response.data;
  }

  async patchInflationPolicy(
    legalEntityId: string,
    data: {
      inflationAdjustmentEnabled?: boolean;
      inflationMathEngine?: string;
      inflationIndexSeriesId?: string | null;
      inflationResultAccountId?: string | null;
    },
  ): Promise<any> {
    const response = await api.patch(
      `/api/v1/accounting/legal-entities/${legalEntityId}/inflation-policy`,
      data,
    );
    return response.data;
  }

  async patchAccountInflation(
    accountId: string,
    data: {
      legalEntityId: string;
      monetaryType?: string;
      inflationAdjustable?: boolean;
    },
  ): Promise<any> {
    const response = await api.patch(
      `/api/v1/accounting/chart/accounts/${accountId}/inflation`,
      data,
    );
    return response.data;
  }

  async listInflationRuns(legalEntityId: string): Promise<any[]> {
    const response = await api.get('/api/v1/accounting/inflation/runs', {
      params: { legalEntityId },
    });
    return response.data;
  }

  async getInflationRun(id: string): Promise<any> {
    const response = await api.get(`/api/v1/accounting/inflation/runs/${id}`);
    return response.data;
  }

  async createInflationRun(data: {
    legalEntityId: string;
    fromPeriod: string;
    toPeriod: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/accounting/inflation/runs', data);
    return response.data;
  }

  async calculateInflationRun(id: string): Promise<any> {
    const response = await api.post(`/api/v1/accounting/inflation/runs/${id}/calculate`);
    return response.data;
  }

  async postInflationRun(id: string): Promise<any> {
    const response = await api.post(`/api/v1/accounting/inflation/runs/${id}/post`);
    return response.data;
  }

  async reverseInflationRun(id: string, reason: string): Promise<any> {
    const response = await api.post(`/api/v1/accounting/inflation/runs/${id}/reverse`, {
      reason,
    });
    return response.data;
  }

  async listWorkingPaperPacks(legalEntityId: string, fiscalYearId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/accounting/working-papers/packs', {
      params: { legalEntityId, fiscalYearId },
    });
    return response.data;
  }

  async createWorkingPaperPack(data: {
    legalEntityId: string;
    fiscalYearId: string;
    title: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/accounting/working-papers/packs', data);
    return response.data;
  }

  async addWorkingPaperItem(packId: string, data: Record<string, unknown>): Promise<any> {
    const response = await api.post(`/api/v1/accounting/working-papers/packs/${packId}/items`, data);
    return response.data;
  }

  async postWorkingPaperAdjustment(itemId: string, entryDate?: string): Promise<any> {
    const response = await api.post(
      `/api/v1/accounting/working-papers/items/${itemId}/post-adjustment`,
      { entryDate },
    );
    return response.data;
  }

  async listAccountingImports(legalEntityId: string): Promise<any[]> {
    const response = await api.get('/api/v1/accounting/imports', {
      params: { legalEntityId },
    });
    return response.data;
  }

  async getAccountingImport(id: string): Promise<any> {
    const response = await api.get(`/api/v1/accounting/imports/${id}`);
    return response.data;
  }

  async uploadAccountingImport(params: {
    legalEntityId: string;
    type: string;
    file: File;
    chartMode?: string;
    entryDate?: string;
  }): Promise<any> {
    const form = new FormData();
    form.append('file', params.file);
    form.append('legalEntityId', params.legalEntityId);
    form.append('type', params.type);
    if (params.chartMode) form.append('chartMode', params.chartMode);
    if (params.entryDate) form.append('entryDate', params.entryDate);
    const response = await api.post('/api/v1/accounting/imports', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async patchAccountingImportMapping(
    id: string,
    body: { columnMapping?: Record<string, string>; options?: Record<string, unknown> },
  ): Promise<any> {
    const response = await api.patch(`/api/v1/accounting/imports/${id}/mapping`, body);
    return response.data;
  }

  async validateAccountingImport(id: string): Promise<any> {
    const response = await api.post(`/api/v1/accounting/imports/${id}/validate`);
    return response.data;
  }

  async confirmAccountingImport(id: string, force = false): Promise<any> {
    const response = await api.post(`/api/v1/accounting/imports/${id}/confirm`, { force });
    return response.data;
  }

  async listCurrencies(includeInactive = false): Promise<any[]> {
    const response = await api.get('/api/v1/accounting/currencies', {
      params: { includeInactive: includeInactive ? 'true' : undefined },
    });
    return response.data;
  }

  async listExchangeRates(
    legalEntityId: string,
    params?: { currency?: string; from?: string; to?: string },
  ): Promise<any[]> {
    const response = await api.get('/api/v1/accounting/exchange-rates', {
      params: { legalEntityId, ...params },
    });
    return response.data;
  }

  async upsertExchangeRate(data: {
    legalEntityId: string;
    currency: string;
    date: string;
    rate: number | string;
    source?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/accounting/exchange-rates', data);
    return response.data;
  }

  async listFxRevaluations(legalEntityId: string): Promise<any[]> {
    const response = await api.get('/api/v1/accounting/fx/revaluations', {
      params: { legalEntityId },
    });
    return response.data;
  }

  async getFxRevaluation(id: string): Promise<any> {
    const response = await api.get(`/api/v1/accounting/fx/revaluations/${id}`);
    return response.data;
  }

  async createFxRevaluation(legalEntityId: string, asOfDate: string): Promise<any> {
    const response = await api.post('/api/v1/accounting/fx/revaluations', {
      legalEntityId,
      asOfDate,
    });
    return response.data;
  }

  async calculateFxRevaluation(id: string): Promise<any> {
    const response = await api.post(`/api/v1/accounting/fx/revaluations/${id}/calculate`);
    return response.data;
  }

  async postFxRevaluation(id: string): Promise<any> {
    const response = await api.post(`/api/v1/accounting/fx/revaluations/${id}/post`);
    return response.data;
  }

  async reverseFxRevaluation(id: string, reason: string): Promise<any> {
    const response = await api.post(`/api/v1/accounting/fx/revaluations/${id}/reverse`, {
      reason,
    });
    return response.data;
  }

  async getFxPolicy(legalEntityId: string): Promise<any> {
    const response = await api.get(`/api/v1/accounting/legal-entities/${legalEntityId}/fx-policy`);
    return response.data;
  }

  async patchFxPolicy(
    legalEntityId: string,
    data: { fxRevaluationEnabled?: boolean },
  ): Promise<any> {
    const response = await api.patch(
      `/api/v1/accounting/legal-entities/${legalEntityId}/fx-policy`,
      data,
    );
    return response.data;
  }

  async downloadAccountingExport(
    report:
      | 'daily-book'
      | 'ledger'
      | 'trial-balance'
      | 'income-statement'
      | 'balance-sheet',
    format: 'xlsx' | 'csv',
    params: Record<string, string | undefined>,
    filename: string,
  ): Promise<void> {
    const response = await api.get(`/api/v1/accounting/reports/${report}.${format}`, {
      params,
      responseType: 'blob',
    });
    const type =
      format === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv;charset=utf-8';
    const blob = new Blob([response.data], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async getApApprovalPolicy(legalEntityId: string): Promise<any> {
    const response = await api.get('/api/v1/payables/approval-policy', {
      params: { legalEntityId },
    });
    return response.data;
  }

  // Branch targets (objetivos)
  async getBranchTargetsDashboard(params: {
    year?: number;
    month?: number;
    branchId?: string;
  } = {}): Promise<any[]> {
    const response = await api.get('/api/v1/branch-targets/dashboard', { params });
    return response.data;
  }

  async upsertBranchTarget(data: {
    branchId: string;
    year: number;
    month: number;
    salesAmountTarget: number;
    salesCountTarget: number;
    collectionTarget: number;
  }): Promise<any> {
    const response = await api.put('/api/v1/branch-targets', data);
    return response.data;
  }

  // Operational reports
  async getDelinquencyReport(params: {
    from: string;
    to: string;
    type?: string;
    branchId?: string;
  }): Promise<any> {
    const response = await api.get('/api/v1/reports/delinquency', { params });
    return response.data;
  }

  async getSalesByPlanReport(params: {
    from: string;
    to: string;
    branchId?: string;
  }): Promise<any[]> {
    const response = await api.get('/api/v1/reports/sales-by-plan', { params });
    return response.data;
  }

  async getSalesByUserReport(params: {
    from: string;
    to: string;
    branchId?: string;
  }): Promise<any> {
    const response = await api.get('/api/v1/reports/sales-by-user', { params });
    return response.data;
  }

  async getMonthlyFinancedReport(params: {
    year?: number;
    branchId?: string;
  } = {}): Promise<any> {
    const response = await api.get('/api/v1/reports/monthly-financed', { params });
    return response.data;
  }

  async downloadOperationalReportXlsx(params: {
    report: string;
    from?: string;
    to?: string;
    year?: number;
    branchId?: string;
    type?: string;
    status?: string;
    userId?: string;
  }): Promise<void> {
    const response = await api.get('/api/v1/reports/export.xlsx', {
      params,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${params.report}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async downloadLegacyReportCsv(params: {
    report: string;
    from?: string;
    to?: string;
    year?: number;
    branchId?: string;
    type?: string;
    status?: string;
    userId?: string;
  }): Promise<void> {
    const response = await api.get('/api/v1/reports/export.csv', {
      params,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${params.report}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async getLegacyReport(reportKey: string, params: Record<string, string | number | undefined> = {}): Promise<any> {
    const response = await api.get(`/api/v1/reports/${reportKey}`, { params });
    return response.data;
  }

  // Fiscal y legal (Fase 5)
  async getTaxableBaseSummary(year: number, month: number, legalEntityId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/fiscal/taxable-base/summary', {
      params: { year, month, legalEntityId },
    });
    return response.data;
  }

  async getTaxableBaseDetail(year: number, month: number, legalEntityId?: string): Promise<any> {
    const response = await api.get('/api/v1/fiscal/taxable-base/detail', {
      params: { year, month, legalEntityId },
    });
    return response.data;
  }

  async downloadTaxableBaseDetailXlsx(params: {
    year: number;
    month: number;
    legalEntityId: string;
  }): Promise<void> {
    const response = await api.get('/api/v1/fiscal/taxable-base/detail.xlsx', {
      params,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `base-imponible-${params.year}-${String(params.month).padStart(2, '0')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async confirmIvaPeriod(data: {
    legalEntityId: string;
    year: number;
    month: number;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/fiscal/taxable-base/confirm-period', data);
    return response.data;
  }

  async processDevengado(year: number, month: number, legalEntityId?: string): Promise<any> {
    const response = await api.post('/api/v1/fiscal/taxable-base/devengado', null, {
      params: { year, month, legalEntityId },
    });
    return response.data;
  }

  async getIvaBookSummary(year: number, month: number, legalEntityId?: string): Promise<any> {
    const response = await api.get('/api/v1/fiscal/iva-book/summary', {
      params: { year, month, legalEntityId },
    });
    return response.data;
  }

  async listIvaBookEntries(year?: number, month?: number): Promise<any[]> {
    const response = await api.get('/api/v1/fiscal/iva-book', { params: { year, month } });
    return response.data;
  }

  async createIvaAdjustment(data: {
    legalEntityId: string;
    periodYear: number;
    periodMonth: number;
    adjustmentAmount: number;
    reason: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/fiscal/iva-adjustments', data);
    return response.data;
  }

  async listLegalEntities(): Promise<any[]> {
    const response = await api.get('/api/v1/legal-entities');
    return response.data;
  }

  async createLegalEntity(data: {
    organizationId: string;
    code: string;
    legalName: string;
    tradeName?: string;
    taxId?: string;
    companyType?: string;
    ivaCondition?: string;
    address?: string;
    baseCurrency?: string;
    isDefault?: boolean;
  }): Promise<any> {
    const response = await api.post('/api/v1/legal-entities', data);
    return response.data;
  }

  async listOrganizations(): Promise<any[]> {
    const response = await api.get('/api/v1/organizations');
    return response.data;
  }

  async listPromissoryNotes(loanId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/promissory-notes', { params: loanId ? { loanId } : undefined });
    return response.data;
  }

  async createPromissoryNote(data: {
    loanId: string;
    noteNumber?: string;
    dueDate?: string;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/promissory-notes', data);
    return response.data;
  }

  async updatePromissoryNoteStatus(
    id: string,
    status: string,
    notes?: string,
  ): Promise<any> {
    const response = await api.patch(`/api/v1/promissory-notes/${id}/status`, { status, notes });
    return response.data;
  }

  async listLoanInsurances(): Promise<any[]> {
    const response = await api.get('/api/v1/loan-insurance');
    return response.data;
  }

  async listBureauAffectations(status?: string): Promise<any[]> {
    const response = await api.get('/api/v1/bureau-affectations', { params: status ? { status } : undefined });
    return response.data;
  }

  async reportBureauAffectation(id: string): Promise<any> {
    const response = await api.patch(`/api/v1/bureau-affectations/${id}/report`);
    return response.data;
  }

  async processBureauAffectationsAuto(): Promise<any> {
    const response = await api.post('/api/v1/bureau-affectations/process-auto');
    return response.data;
  }

  async createBureauAffectation(data: {
    documentNumber: string;
    loanId?: string;
    customerId?: string;
    provider?: string;
    affectationType?: string;
    overdueDays?: number;
    overdueAmount?: number;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/bureau-affectations', data);
    return response.data;
  }

  // Integraciones Veraz + AFIP
  async getVerazStatus(): Promise<any> {
    const response = await api.get('/api/v1/integrations/veraz/status');
    return response.data;
  }

  async getPypStatus(): Promise<any> {
    const response = await api.get('/api/v1/integrations/pyp/status');
    return response.data;
  }

  async getApiccStatus(): Promise<any> {
    const response = await api.get('/api/v1/integrations/apicc/status');
    return response.data;
  }

  async getAfipIntegrationStatus(): Promise<any> {
    const response = await api.get('/api/v1/integrations/electronic-invoicing/status');
    return response.data;
  }

  async listElectronicInvoices(params?: { status?: string; legalEntityId?: string }): Promise<any[]> {
    const response = await api.get('/api/v1/integrations/electronic-invoicing/invoices', { params });
    return response.data;
  }

  async downloadElectronicInvoicePdf(invoiceId: string, filename: string): Promise<void> {
    const response = await api.get(`/api/v1/integrations/electronic-invoicing/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async getAfipConfig(legalEntityId: string): Promise<any> {
    const response = await api.get(`/api/v1/integrations/electronic-invoicing/afip-config/${legalEntityId}`);
    return response.data;
  }

  async updateAfipConfig(
    legalEntityId: string,
    data: {
      cuit?: string;
      pointOfSale?: number;
      integrationMode?: string;
      environment?: string;
      defaultInvoiceType?: string;
      certificateRef?: string;
      billInterest?: boolean;
      billPenalty?: boolean;
      billPrincipal?: boolean;
      isActive?: boolean;
    },
  ): Promise<any> {
    const response = await api.put(`/api/v1/integrations/electronic-invoicing/afip-config/${legalEntityId}`, data);
    return response.data;
  }

  async syncAfipLastInvoiceNumber(legalEntityId: string): Promise<any> {
    const response = await api.post(
      `/api/v1/integrations/electronic-invoicing/afip-config/${legalEntityId}/sync-last-number`,
    );
    return response.data;
  }

  async uploadAfipCertificate(
    legalEntityId: string,
    files: { cert?: File; key?: File; p12?: File },
    p12Password?: string,
  ): Promise<any> {
    const form = new FormData();
    if (files.cert) form.append('cert', files.cert);
    if (files.key) form.append('key', files.key);
    if (files.p12) form.append('p12', files.p12);
    if (p12Password) form.append('p12Password', p12Password);
    const response = await api.post(
      `/api/v1/integrations/electronic-invoicing/afip-config/${legalEntityId}/certificate`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }

  async listElectronicInvoiceQueue(params?: { legalEntityId?: string; status?: string }): Promise<any[]> {
    const response = await api.get('/api/v1/integrations/electronic-invoicing/queue', { params });
    return response.data;
  }

  async processElectronicInvoiceQueue(data?: { legalEntityId?: string; limit?: number }): Promise<any[]> {
    const response = await api.post('/api/v1/integrations/electronic-invoicing/queue/process', data ?? {});
    return response.data;
  }

  async listElectronicInvoiceAttempts(invoiceId: string): Promise<any[]> {
    const response = await api.get(`/api/v1/integrations/electronic-invoicing/invoices/${invoiceId}/attempts`);
    return response.data;
  }

  async retryElectronicInvoice(id: string): Promise<any> {
    const response = await api.post(`/api/v1/integrations/electronic-invoicing/invoices/${id}/retry`);
    return response.data;
  }

  async testVerazQuery(documentNumber: string): Promise<any> {
    const response = await api.post('/api/v1/integrations/veraz/test-query', { documentNumber });
    return response.data;
  }

  // CRM y marketing (Fase 6)
  async listSocialLeads(status?: string): Promise<any[]> {
    const response = await api.get('/api/v1/crm/social-leads', { params: status ? { status } : undefined });
    return response.data;
  }

  async createSocialLead(data: Record<string, string>): Promise<any> {
    const response = await api.post('/api/v1/crm/social-leads', data);
    return response.data;
  }

  async convertSocialLead(id: string, requestedAmount?: number): Promise<any> {
    const response = await api.post(`/api/v1/crm/social-leads/${id}/convert`, { requestedAmount });
    return response.data;
  }

  async listReferralPromotions(): Promise<any[]> {
    const response = await api.get('/api/v1/crm/referral-promotions');
    return response.data;
  }

  async listCustomerReferrals(status?: string): Promise<any[]> {
    const response = await api.get('/api/v1/crm/referrals', { params: status ? { status } : undefined });
    return response.data;
  }

  async registerCustomerReferral(data: {
    referrerCustomerId: string;
    referredDocumentNumber: string;
    promotionCode?: string;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/crm/referrals', data);
    return response.data;
  }

  async listLoyaltyAccounts(minBalance = 0): Promise<any[]> {
    const response = await api.get('/api/v1/crm/loyalty/accounts', { params: { minBalance } });
    return response.data;
  }

  async getLoyaltyAccount(customerId: string): Promise<any> {
    const response = await api.get(`/api/v1/crm/loyalty/customers/${customerId}`);
    return response.data;
  }

  async adjustLoyaltyPoints(data: { customerId: string; points: number; notes: string }): Promise<any> {
    const response = await api.post('/api/v1/crm/loyalty/adjust', data);
    return response.data;
  }

  async redeemLoyaltyPoints(data: { customerId: string; points: number; notes: string }): Promise<any> {
    const response = await api.post('/api/v1/crm/loyalty/redeem', data);
    return response.data;
  }

  async listSweepstakes(status?: string): Promise<any[]> {
    const response = await api.get('/api/v1/crm/sweepstakes', { params: status ? { status } : undefined });
    return response.data;
  }

  async createSweepstakes(data: {
    name: string;
    description?: string;
    startDate: string;
    endDate: string;
    pointsPerEntry?: number;
    maxEntriesPerCustomer?: number;
  }): Promise<any> {
    const response = await api.post('/api/v1/crm/sweepstakes', data);
    return response.data;
  }

  async enterSweepstakes(sweepstakesId: string, customerId: string): Promise<any> {
    const response = await api.post(`/api/v1/crm/sweepstakes/${sweepstakesId}/enter`, { customerId });
    return response.data;
  }

  async drawSweepstakes(sweepstakesId: string): Promise<any> {
    const response = await api.post(`/api/v1/crm/sweepstakes/${sweepstakesId}/draw`);
    return response.data;
  }

  // Loan products
  async getLoanProducts(opts: { activeOnly?: boolean; branchId?: string } = {}): Promise<any[]> {
    const params: Record<string, string> = {
      activeOnly: opts.activeOnly === false ? 'false' : 'true',
    };
    if (opts.branchId) params.branchId = opts.branchId;
    const response = await api.get('/api/v1/loan-products', { params });
    return response.data;
  }

  async simulateLoanProduct(
    productId: string,
    data: { amount: number; termMonths: number; startDate?: string; interestRate?: number; branchId?: string },
  ): Promise<any> {
    const response = await api.post(`/api/v1/loan-products/${productId}/simulate`, data);
    return response.data;
  }

  async getLoanProduct(id: string): Promise<any> {
    const response = await api.get(`/api/v1/loan-products/${id}`);
    return response.data;
  }

  async createLoanProduct(data: {
    name: string;
    shortName?: string;
    description?: string;
    minAmount: number;
    maxAmount: number;
    minTermMonths: number;
    maxTermMonths: number;
    interestRate: number;
    tea?: number;
    adminFeePercent?: number;
    installmentAdminFixed?: number;
    installmentAdminPercent?: number;
    installmentAdminCap?: number | null;
    quotaPercent?: number;
    remainderPercent?: number;
    monthsToFirstDue?: number;
    currency?: string;
    planCode?: string;
    planType?: string;
    graceDays?: number;
    fixedDueDay?: number;
    penaltyStartDay?: number | null;
    allowParallelLoan?: boolean;
    minOverdueInstallmentsForRefinance?: number;
    minCancelledLoansRequired?: number;
    discountCodeId?: string;
    branchIds?: string[];
  }): Promise<any> {
    const response = await api.post('/api/v1/loan-products', data);
    return response.data;
  }

  async updateLoanProduct(id: string, data: Partial<{
    name: string;
    shortName: string;
    description: string;
    minAmount: number;
    maxAmount: number;
    minTermMonths: number;
    maxTermMonths: number;
    interestRate: number;
    tea: number;
    adminFeePercent: number;
    installmentAdminFixed: number;
    installmentAdminPercent: number;
    installmentAdminCap: number | null;
    quotaPercent: number;
    remainderPercent: number;
    monthsToFirstDue: number;
    currency: string;
    isActive: boolean;
    planCode: string;
    planType: string;
    graceDays: number;
    fixedDueDay: number;
    penaltyStartDay: number | null;
    allowParallelLoan: boolean;
    minOverdueInstallmentsForRefinance: number;
    minCancelledLoansRequired: number;
    discountCodeId: string;
    branchIds: string[];
    installmentPricingMode: 'FORMULA' | 'BREAKDOWN';
  }>): Promise<any> {
    const response = await api.put(`/api/v1/loan-products/${id}`, data);
    return response.data;
  }

  async addProductValidity(productId: string, data: { validFrom: string; validTo?: string; notes?: string }): Promise<any> {
    const response = await api.post(`/api/v1/loan-products/${productId}/validities`, data);
    return response.data;
  }

  async endProductValidity(productId: string, validityId: string, data?: { validTo?: string; notes?: string }): Promise<any> {
    const response = await api.patch(`/api/v1/loan-products/${productId}/validities/${validityId}/end`, data ?? {});
    return response.data;
  }

  async setProductBranches(productId: string, branchIds: string[]): Promise<any> {
    const response = await api.put(`/api/v1/loan-products/${productId}/branches`, { branchIds });
    return response.data;
  }

  async setProductCoefficients(productId: string, coefficients: { termMonths: number; coefficient: number }[]): Promise<any> {
    const response = await api.put(`/api/v1/loan-products/${productId}/coefficients`, { coefficients });
    return response.data;
  }

  async setProductCharges(
    productId: string,
    charges: Array<{
      code: string;
      name: string;
      kind?: string;
      valueMode: 'FIXED' | 'PERCENT';
      base?: 'INSTALLMENT' | 'LOAN_PRINCIPAL';
      value: number;
      isBalancing?: boolean;
      sortOrder?: number;
      isActive?: boolean;
    }>,
  ): Promise<any> {
    const response = await api.put(`/api/v1/loan-products/${productId}/charges`, { charges });
    return response.data;
  }

  async deactivateLoanProduct(id: string): Promise<void> {
    await api.delete(`/api/v1/loan-products/${id}`);
  }

  async activateLoanProduct(id: string): Promise<any> {
    const response = await api.post(`/api/v1/loan-products/${id}/activate`);
    return response.data;
  }

  // Payments
  async listPayments(params?: {
    page?: number;
    pageSize?: number;
    customerId?: string;
    loanId?: string;
  }): Promise<{ data: any[]; meta: { total: number; page: number; pageSize: number; pageCount: number } }> {
    const response = await api.get('/api/v1/payments', { params });
    const body = response.data?.data && response.data?.meta ? response.data : response.data;
    if (Array.isArray(body)) {
      return {
        data: body,
        meta: { total: body.length, page: 1, pageSize: body.length || 25, pageCount: 1 },
      };
    }
    return {
      data: body?.data ?? [],
      meta: body?.meta ?? { total: 0, page: 1, pageSize: 25, pageCount: 1 },
    };
  }

  /** Compat: primeros resultados (máx 100). Preferí listPayments en listados. */
  async getPayments(customerId?: string): Promise<any[]> {
    const { data } = await this.listPayments({
      page: 1,
      pageSize: 100,
      ...(customerId ? { customerId } : {}),
    });
    return data;
  }

  async getPayment(id: string): Promise<any> {
    const response = await api.get(`/api/v1/payments/${id}`);
    return response.data;
  }

  async createPayment(data: {
    loanId: string;
    installmentId?: string;
    amount: number;
    paymentDate?: string;
    paymentMethod?: string;
    notes?: string;
    cashSessionId?: string;
    cashRegisterId?: string;
    bankAccountId?: string;
    branchId?: string;
    externalId?: string;
    paymentMethods?: Array<{
      method: string;
      amount: number;
      currency?: string;
      transactionNumber?: string;
      bankName?: string;
    }>;
  }): Promise<any> {
    const response = await api.post('/api/v1/payments', data);
    return response.data;
  }

  async previewPayment(data: {
    loanId: string;
    amount: number;
    installmentIds?: string[];
    paymentDate?: string;
    skipLivePenalty?: boolean;
  }): Promise<any> {
    const response = await api.post('/api/v1/payments/preview', data);
    return response.data;
  }

  async getLoanFinancialSummary(loanId: string): Promise<any> {
    const response = await api.get(`/api/v1/loans/${loanId}/financial-summary`);
    return response.data;
  }

  async searchPayments(params: {
    branchId?: string;
    fromDate?: string;
    toDate?: string;
    status?: string;
    voucherNumber?: string;
    loanNumber?: string;
    documentNumber?: string;
  }): Promise<any[]> {
    const response = await api.get('/api/v1/payments/search', { params });
    return response.data;
  }

  async voidPayment(paymentId: string, data: { reason: string; cashSessionId?: string }): Promise<any> {
    const response = await api.post(`/api/v1/payments/${paymentId}/void`, data);
    return response.data;
  }

  getVoucherPrintUrl(voucherId: string): string {
    const base = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    return `${base}/api/v1/payments/vouchers/${voucherId}/print`;
  }

  async openVoucherPrint(voucherId: string): Promise<void> {
    const response = await api.get(`/api/v1/payments/vouchers/${voucherId}/print`, {
      responseType: 'text',
    });
    const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=800,height=600');
    if (!printWindow) {
      throw new Error('El navegador bloqueó la ventana de impresión. Permití popups para este sitio.');
    }
    printWindow.document.write(response.data);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = () => {
      printWindow.print();
    };
    try {
      await this.markVoucherPrinted(voucherId);
    } catch {
      // best-effort
    }
  }

  async getVoucher(voucherId: string): Promise<any> {
    const response = await api.get(`/api/v1/payments/vouchers/${voucherId}`);
    return response.data;
  }

  async markVoucherPrinted(voucherId: string): Promise<any> {
    const response = await api.post(`/api/v1/payments/vouchers/${voucherId}/printed`);
    return response.data;
  }

  async getPenaltyRate(): Promise<{ monthlyRate: number }> {
    const response = await api.get('/api/v1/settings/penalty-rate');
    return response.data;
  }

  async updatePenaltyRate(monthlyRate: number): Promise<{ monthlyRate: number }> {
    const response = await api.patch('/api/v1/settings/penalty-rate', { monthlyRate });
    return response.data;
  }

  async listPaymentPromises(loanId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/collections/promises', {
      params: loanId ? { loanId } : undefined,
    });
    return response.data;
  }

  async createPaymentPromise(data: {
    loanId: string;
    promisedAmount: number;
    promisedDate: string;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/collections/promises', data);
    return response.data;
  }

  async updatePaymentPromiseStatus(id: string, status: string): Promise<any> {
    const response = await api.patch(`/api/v1/collections/promises/${id}/status`, { status });
    return response.data;
  }

  async listCollectionLetters(loanId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/collections/letters', {
      params: loanId ? { loanId } : undefined,
    });
    return response.data;
  }

  async createCollectionLetter(data: {
    loanId: string;
    letterType: string;
    status?: string;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/collections/letters', data);
    return response.data;
  }

  async listCollectionContacts(customerId?: string, loanId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/collections/contacts', {
      params: { customerId, loanId },
    });
    return response.data;
  }

  async createCollectionContact(data: {
    customerId: string;
    loanId?: string;
    channel: string;
    outcome?: string;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/collections/contacts', data);
    return response.data;
  }

  // Branches
  async getBranches(activeOnly = false, legalEntityId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/branches', {
      params: {
        activeOnly: activeOnly ? 'true' : undefined,
        legalEntityId: legalEntityId || undefined,
      },
    });
    return response.data;
  }

  async createBranch(data: {
    code: string;
    name: string;
    legalEntityId: string;
    zone?: string;
    address?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/branches', data);
    return response.data;
  }

  // Cash registers
  async getCashRegisters(
    branchId?: string,
    legalEntityId?: string,
    opts?: { forSession?: boolean; type?: string; includeInactive?: boolean },
  ): Promise<any[]> {
    const response = await api.get('/api/v1/cash-registers', {
      params: {
        ...(branchId ? { branchId } : {}),
        ...(legalEntityId ? { legalEntityId } : {}),
        ...(opts?.forSession ? { forSession: true } : {}),
        ...(opts?.type ? { type: opts.type } : {}),
        ...(opts?.includeInactive ? { includeInactive: true } : {}),
      },
    });
    return response.data;
  }

  async getTreasurySummary(legalEntityId: string): Promise<any> {
    const response = await api.get('/api/v1/treasury/summary', { params: { legalEntityId } });
    return response.data;
  }

  async getTreasuryPolicy(legalEntityId: string): Promise<any> {
    const response = await api.get('/api/v1/treasury/policy', { params: { legalEntityId } });
    return response.data;
  }

  async updateTreasuryPolicy(
    legalEntityId: string,
    data: {
      transferApprovalThreshold?: number;
      allowSelfApproval?: boolean;
      allowOperativeDirectTransfer?: boolean;
    },
  ): Promise<any> {
    const response = await api.patch('/api/v1/treasury/policy', data, { params: { legalEntityId } });
    return response.data;
  }

  async listCashBankTransfers(legalEntityId?: string, status?: string): Promise<any[]> {
    const response = await api.get('/api/v1/treasury/cash-bank-transfers', {
      params: { legalEntityId, status },
    });
    return response.data;
  }

  async requestCashBankTransfer(data: {
    direction: 'DEPOSIT' | 'WITHDRAWAL';
    cashRegisterId: string;
    bankAccountId: string;
    amount: number;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/treasury/cash-bank-transfers', data);
    return response.data;
  }

  async approveCashBankTransfer(id: string): Promise<any> {
    const response = await api.post(`/api/v1/treasury/cash-bank-transfers/${id}/approve`);
    return response.data;
  }

  async rejectCashBankTransfer(id: string, reason?: string): Promise<any> {
    const response = await api.post(`/api/v1/treasury/cash-bank-transfers/${id}/reject`, { reason });
    return response.data;
  }

  async getRegisterBankAccounts(registerId: string): Promise<any[]> {
    const response = await api.get(`/api/v1/treasury/registers/${registerId}/bank-accounts`);
    return response.data;
  }

  async setRegisterBankAccounts(
    registerId: string,
    links: { bankAccountId: string; isDefault?: boolean }[],
  ): Promise<any[]> {
    const response = await api.put(`/api/v1/treasury/registers/${registerId}/bank-accounts`, { links });
    return response.data;
  }

  async listTreasuryTransfers(legalEntityId?: string, status?: string): Promise<any[]> {
    const response = await api.get('/api/v1/treasury/transfers', {
      params: { ...(legalEntityId ? { legalEntityId } : {}), ...(status ? { status } : {}) },
    });
    return response.data;
  }

  async requestTreasuryTransfer(data: {
    fromRegisterId: string;
    toRegisterId: string;
    amount: number;
    notes?: string;
    conceptId?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/treasury/transfers', data);
    return response.data;
  }

  async approveTreasuryTransfer(id: string): Promise<any> {
    const response = await api.post(`/api/v1/treasury/transfers/${id}/approve`);
    return response.data;
  }

  async rejectTreasuryTransfer(id: string, reason?: string): Promise<any> {
    const response = await api.post(`/api/v1/treasury/transfers/${id}/reject`, { reason });
    return response.data;
  }

  async listTreasuryAdjustments(legalEntityId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/treasury/adjustments', {
      params: legalEntityId ? { legalEntityId } : undefined,
    });
    return response.data;
  }

  async createTreasuryAdjustment(data: {
    cashRegisterId: string;
    direction: 'IN' | 'OUT';
    amount: number;
    reason: string;
    conceptId?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/treasury/adjustments', data);
    return response.data;
  }

  async approveTreasuryAdjustment(id: string): Promise<any> {
    const response = await api.post(`/api/v1/treasury/adjustments/${id}/approve`);
    return response.data;
  }

  async createCashRegister(data: {
    type?: 'OPERATIVE' | 'BRANCH_MASTER' | 'CENTRAL_TREASURY';
    legalEntityId?: string;
    branchId?: string;
    number: string;
    name?: string;
    code?: string;
    currency?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/cash-registers', data);
    return response.data;
  }

  async updateCashRegister(
    id: string,
    data: {
      name?: string;
      number?: string;
      isActive?: boolean;
      allowNegativeBalance?: boolean;
    },
  ): Promise<any> {
    const response = await api.patch(`/api/v1/cash-registers/${id}`, data);
    return response.data;
  }

  async openCashSession(data: { cashRegisterId: string; openingBalance: number }): Promise<any> {
    const response = await api.post('/api/v1/cash-registers/sessions/open', data);
    return response.data;
  }

  async getMyCashSession(): Promise<any | null> {
    const response = await api.get('/api/v1/cash-registers/sessions/me');
    const data = response.data;
    if (!data || typeof data !== 'object' || !data.id) return null;
    return data;
  }

  async closeCashSession(
    sessionId: string,
    closingBalance: number,
    notes?: string,
  ): Promise<any> {
    const response = await api.post(`/api/v1/cash-registers/sessions/${sessionId}/close`, {
      closingBalance,
      ...(notes ? { notes } : {}),
    });
    return response.data;
  }

  async getCashSession(sessionId: string): Promise<any> {
    const response = await api.get(`/api/v1/cash-registers/sessions/${sessionId}`);
    return response.data;
  }

  async listPendingAuditSessions(): Promise<any[]> {
    const response = await api.get('/api/v1/cash-registers/sessions/pending-audit');
    return response.data;
  }

  async auditCashSession(sessionId: string, data: {
    countedAmount: number;
    notes?: string;
    denominations?: { denomination: number; quantity: number }[];
  }): Promise<any> {
    const response = await api.post(`/api/v1/cash-registers/sessions/${sessionId}/audit`, data);
    return response.data;
  }

  async addCashMovement(sessionId: string, data: {
    type: 'IN' | 'OUT';
    amount: number;
    conceptId: string;
    description?: string;
    reference?: string;
  }): Promise<any> {
    const response = await api.post(`/api/v1/cash-registers/sessions/${sessionId}/movements`, data);
    return response.data;
  }

  async listCashMovements(params: {
    sessionId?: string;
    branchId?: string;
    cashRegisterId?: string;
    type?: string;
    conceptId?: string;
    from?: string;
    to?: string;
  } = {}): Promise<any[]> {
    const response = await api.get('/api/v1/cash-registers/movements', { params });
    return response.data;
  }

  async transferCash(data: {
    toSessionId: string;
    amount: number;
    conceptId?: string;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/cash-registers/transfers', data);
    return response.data;
  }

  async getCashRegisterLedgerBalance(registerId: string): Promise<{ cashRegisterId: string; balance: number }> {
    const response = await api.get(`/api/v1/treasury/registers/${registerId}/balance`);
    return response.data;
  }

  async getCashSessionBalance(sessionId: string): Promise<any> {
    const response = await api.get(`/api/v1/cash-registers/sessions/${sessionId}/balance`);
    return response.data;
  }

  async listCashConcepts(): Promise<any[]> {
    const response = await api.get('/api/v1/cash-registers/concepts');
    return response.data;
  }

  async getCashDenominations(): Promise<{ denomination: number }[]> {
    const response = await api.get('/api/v1/cash-registers/denominations');
    return response.data;
  }

  async getRecaudacionReport(params: { branchId?: string; cashRegisterId?: string; from?: string; to?: string } = {}): Promise<any> {
    const response = await api.get('/api/v1/cash-registers/reports/recaudacion', { params });
    return response.data;
  }

  async getRealCollectionReport(params: { branchId?: string; cashRegisterId?: string; from?: string; to?: string } = {}): Promise<any> {
    const response = await api.get('/api/v1/cash-registers/reports/cobranza-real', { params });
    return response.data;
  }

  async getArqueosReport(params: { branchId?: string; cashRegisterId?: string; from?: string; to?: string } = {}): Promise<any[]> {
    const response = await api.get('/api/v1/cash-registers/reports/arqueos', { params });
    return response.data;
  }

  async listOpenCashSessions(): Promise<any[]> {
    const registers = await this.getCashRegisters();
    return registers.flatMap((r) => (r.sessions ?? []).map((s: any) => ({ ...s, cashRegister: r })));
  }

  async listCashPermissions(cashRegisterId: string): Promise<any[]> {
    const response = await api.get('/api/v1/cash-registers/permissions', { params: { cashRegisterId } });
    return response.data;
  }

  async upsertCashPermission(data: {
    userId: string;
    cashRegisterId: string;
    canOpen?: boolean;
    canClose?: boolean;
    canMove?: boolean;
    canTransfer?: boolean;
    canAudit?: boolean;
    canAdjust?: boolean;
    canApprove?: boolean;
    isDefault?: boolean;
  }): Promise<any> {
    const response = await api.patch('/api/v1/cash-registers/permissions', data);
    return response.data;
  }

  async listSystemParameters(): Promise<any[]> {
    const response = await api.get('/api/v1/settings/parameters');
    return response.data;
  }

  async getSystemParameter(key: string): Promise<any> {
    const response = await api.get(`/api/v1/settings/parameters/${key}`);
    return response.data;
  }

  async updateSystemParameter(key: string, data: { value: string; description?: string; category?: string }): Promise<any> {
    const response = await api.patch(`/api/v1/settings/parameters/${key}`, data);
    return response.data;
  }

  async getBureauAffectationDays(): Promise<{ days: number }> {
    const response = await api.get('/api/v1/settings/bureau-affectation-days');
    return response.data;
  }

  async updateBureauAffectationDays(days: number): Promise<{ days: number }> {
    const response = await api.patch('/api/v1/settings/bureau-affectation-days', { days });
    return response.data;
  }

  async getBureauAffectationProvider(): Promise<{ provider: string }> {
    const response = await api.get('/api/v1/settings/bureau-affectation-provider');
    return response.data;
  }

  async updateBureauAffectationProvider(provider: string): Promise<{ provider: string }> {
    const response = await api.patch('/api/v1/settings/bureau-affectation-provider', { provider });
    return response.data;
  }

  async getMailStatus(branchId?: string): Promise<any> {
    const response = await api.get('/api/v1/settings/mail-status', {
      params: branchId ? { branchId } : undefined,
    });
    return response.data;
  }

  async sendTestMail(to: string, branchId?: string): Promise<any> {
    const response = await api.post('/api/v1/settings/test-mail', { to, branchId });
    return response.data;
  }

  async getOrganizationNotificationConfig(): Promise<any> {
    const response = await api.get('/api/v1/settings/notification-config');
    return response.data;
  }

  async updateOrganizationNotificationConfig(data: Record<string, unknown>): Promise<any> {
    const response = await api.put('/api/v1/settings/notification-config', data);
    return response.data;
  }

  async testOrganizationMail(to: string): Promise<any> {
    const response = await api.post('/api/v1/settings/notification-config/test-mail', { to });
    return response.data;
  }

  async testOrganizationWhatsApp(to: string): Promise<any> {
    const response = await api.post('/api/v1/settings/notification-config/test-whatsapp', { to });
    return response.data;
  }

  async listBranchNotificationConfigs(): Promise<any[]> {
    const response = await api.get('/api/v1/branches/notification-configs');
    return response.data;
  }

  async getBranchNotificationConfig(branchId: string): Promise<any> {
    const response = await api.get(`/api/v1/branches/${branchId}/notification-config`);
    return response.data;
  }

  async updateBranchNotificationConfig(branchId: string, data: Record<string, unknown>): Promise<any> {
    const response = await api.put(`/api/v1/branches/${branchId}/notification-config`, data);
    return response.data;
  }

  async testBranchMail(branchId: string, to: string): Promise<any> {
    const response = await api.post(`/api/v1/branches/${branchId}/notification-config/test-mail`, { to });
    return response.data;
  }

  async testBranchWhatsApp(branchId: string, to: string, message?: string): Promise<any> {
    const response = await api.post(`/api/v1/branches/${branchId}/notification-config/test-whatsapp`, {
      to,
      message,
    });
    return response.data;
  }

  async listStaffUsers(): Promise<any[]> {
    const response = await api.get('/api/v1/users');
    return response.data;
  }

  async createStaffUser(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    role: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/users', data);
    return response.data;
  }

  async updateStaffUser(
    userId: string,
    data: {
      email?: string;
      password?: string;
      firstName?: string;
      lastName?: string;
      phone?: string | null;
      role?: string;
      isActive?: boolean;
    },
  ): Promise<any> {
    const response = await api.patch(`/api/v1/users/${userId}`, data);
    return response.data;
  }

  async getMyPermissions(): Promise<any> {
    const response = await api.get('/api/v1/users/me/permissions');
    return response.data;
  }

  async getPermissionsCatalog(): Promise<any> {
    const response = await api.get('/api/v1/users/permissions/catalog');
    return response.data;
  }

  async getUserPermissions(userId: string): Promise<any> {
    const response = await api.get(`/api/v1/users/${userId}/permissions`);
    return response.data;
  }

  async setUserBranches(userId: string, branches: { branchId: string; canOperate?: boolean }[]): Promise<any> {
    const response = await api.put(`/api/v1/users/${userId}/branches`, { branches });
    return response.data;
  }

  async setUserCashAccess(
    userId: string,
    cashAccess: {
      cashRegisterId: string;
      canOpen?: boolean;
      canClose?: boolean;
      canMove?: boolean;
      canTransfer?: boolean;
      canAudit?: boolean;
      isDefault?: boolean;
    }[],
  ): Promise<any> {
    const response = await api.put(`/api/v1/users/${userId}/cash-access`, { cashAccess });
    return response.data;
  }

  async setUserPermissions(userId: string, grants: { module: string; action: string; granted: boolean }[]): Promise<any> {
    const response = await api.put(`/api/v1/users/${userId}/permissions`, { grants });
    return response.data;
  }

  async applyLegacyRole(userId: string, legacyRole: string): Promise<any> {
    const response = await api.post(`/api/v1/users/${userId}/apply-legacy-role`, { legacyRole });
    return response.data;
  }

  // Collections
  async getCollections(params: { status?: string; loanId?: string } = {}): Promise<any[]> {
    const response = await api.get('/api/v1/collections', { params });
    return response.data;
  }

  async runCollectionsJob(): Promise<{ installmentsMarkedOverdue: number; collectionsCreated: number; collectionsAlreadyExisting: number }> {
    const response = await api.post('/api/v1/collections/run');
    return response.data;
  }

  async getCollectionWorkstation(documentNumber: string): Promise<any> {
    const response = await api.get('/api/v1/collections/workstation', {
      params: { documentNumber },
    });
    return response.data;
  }

  async listCollectionNotes(params: {
    customerId?: string;
    loanId?: string;
    from?: string;
    to?: string;
  } = {}): Promise<any[]> {
    const response = await api.get('/api/v1/collections/notes', { params });
    return response.data;
  }

  async createCollectionNote(data: {
    loanId: string;
    customerId: string;
    type?: string;
    description: string;
    notifyCustomer?: boolean;
  }): Promise<any> {
    const response = await api.post('/api/v1/collections/notes', data);
    return response.data;
  }

  async getCollectionTimeline(loanId: string): Promise<any[]> {
    const response = await api.get(`/api/v1/collections/timeline/${loanId}`);
    return response.data;
  }

  async listEmploymentVerifications(params: {
    customerId?: string;
    loanId?: string;
    status?: string;
  } = {}): Promise<any[]> {
    const response = await api.get('/api/v1/collections/verifications', { params });
    return response.data;
  }

  async createEmploymentVerification(data: {
    customerId: string;
    loanId?: string;
    employerName?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/collections/verifications', data);
    return response.data;
  }

  async updateEmploymentVerification(
    id: string,
    data: { status?: string; result?: string },
  ): Promise<any> {
    const response = await api.patch(`/api/v1/collections/verifications/${id}`, data);
    return response.data;
  }

  async applyCollectionAction(data: {
    loanId: string;
    action: string;
    nextCollectionAt?: string;
    whatsappPreferred?: boolean;
    skipCollectionLetter?: boolean;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/api/v1/collections/actions', data);
    return response.data;
  }

  async sweepCollectionNotes(data: {
    branchId: string;
    overdueDays: number;
    descriptionTemplate?: string;
  }): Promise<{ created: number; loansProcessed: number }> {
    const response = await api.post('/api/v1/collections/sweeps/notes', data);
    return response.data;
  }

  async sweepEmploymentVerifications(data: {
    branchId?: string;
    overdueDays?: number;
  } = {}): Promise<{ created: number; skipped: number; loansScanned: number }> {
    const response = await api.post('/api/v1/collections/sweeps/verifications', data);
    return response.data;
  }

  // Accounting
  async getAccounts(): Promise<any[]> {
    const response = await api.get('/api/v1/accounting/accounts');
    return response.data;
  }

  async getAccountingBalance(legalEntityId: string): Promise<any> {
    const response = await api.get('/api/v1/accounting/balance', {
      params: { legalEntityId },
    });
    return response.data;
  }

  async getAccountingChart(legalEntityId: string): Promise<any[]> {
    const response = await api.get(`/api/v1/accounting/chart/${legalEntityId}`);
    return response.data;
  }

  async createAccountingAccount(
    legalEntityId: string,
    data: {
      code: string;
      name: string;
      parentId?: string;
      type: string;
      nature: string;
      isPosting?: boolean;
      description?: string;
    },
  ): Promise<any> {
    const response = await api.post(`/api/v1/accounting/chart/${legalEntityId}/accounts`, data);
    return response.data;
  }

  async updateAccountingAccount(
    id: string,
    data: {
      legalEntityId: string;
      name?: string;
      description?: string | null;
      isPosting?: boolean;
      isActive?: boolean;
    },
  ): Promise<any> {
    const response = await api.patch(`/api/v1/accounting/chart/accounts/${id}`, data);
    return response.data;
  }

  async setAccountingAccountActive(
    id: string,
    legalEntityId: string,
    isActive: boolean,
  ): Promise<any> {
    const response = await api.post(`/api/v1/accounting/chart/accounts/${id}/activate`, {
      legalEntityId,
      isActive,
    });
    return response.data;
  }

  async listConceptMappings(legalEntityId: string, includeInactive = true): Promise<any[]> {
    const response = await api.get('/api/v1/accounting/concept-mappings', {
      params: { legalEntityId, includeInactive: includeInactive ? 'true' : undefined },
    });
    return response.data;
  }

  async upsertConceptMapping(data: {
    legalEntityId: string;
    conceptCode: string;
    debitAccountId: string;
    creditAccountId: string;
    isActive?: boolean;
  }): Promise<any> {
    const response = await api.post('/api/v1/accounting/concept-mappings', data);
    return response.data;
  }

  async setConceptMappingActive(
    id: string,
    legalEntityId: string,
    isActive: boolean,
  ): Promise<any> {
    const response = await api.post(`/api/v1/accounting/concept-mappings/${id}/activate`, {
      legalEntityId,
      isActive,
    });
    return response.data;
  }

  async getAccountingPeriods(legalEntityId: string): Promise<any[]> {
    const response = await api.get(`/api/v1/accounting/periods/${legalEntityId}`);
    return response.data;
  }

  async getAccountingJournal(params: {
    legalEntityId: string;
    dateFrom: string;
    dateTo: string;
    branchId?: string;
  }): Promise<any[]> {
    const response = await api.get('/api/v1/accounting/journal', { params });
    return response.data;
  }

  async createAccountingJournalDraft(data: {
    legalEntityId: string;
    branchId?: string;
    entryDate: string;
    description: string;
    reference?: string;
    lines: Array<{
      accountId: string;
      debit?: number;
      credit?: number;
      description?: string;
      branchId?: string;
      costCenterId?: string;
      businessUnitId?: string;
    }>;
  }): Promise<any> {
    const response = await api.post('/api/v1/accounting/journal/draft', data);
    return response.data;
  }

  async postAccountingJournal(id: string): Promise<any> {
    const response = await api.post(`/api/v1/accounting/journal/${id}/post`);
    return response.data;
  }

  async getAccountingLedger(params: {
    legalEntityId: string;
    accountId: string;
    dateFrom: string;
    dateTo: string;
  }): Promise<any> {
    const response = await api.get('/api/v1/accounting/ledger', { params });
    return response.data;
  }

  async getTrialBalance(params: {
    legalEntityId: string;
    dateFrom: string;
    dateTo: string;
    branchId?: string;
    costCenterId?: string;
    businessUnitId?: string;
    includeReversed?: boolean;
    mode?: 'period' | 'accumulated';
    compareFrom?: string;
    compareTo?: string;
  }): Promise<any> {
    const response = await api.get('/api/v1/accounting/reports/trial-balance', {
      params: {
        ...params,
        includeReversed: params.includeReversed ? 'true' : undefined,
      },
    });
    return response.data;
  }

  async getAccountingDashboard(legalEntityId: string, asOf?: string): Promise<any> {
    const response = await api.get('/api/v1/accounting/dashboard', {
      params: { legalEntityId, ...(asOf ? { asOf } : {}) },
    });
    return response.data;
  }

  async getAccountingDailyBook(params: {
    legalEntityId: string;
    dateFrom: string;
    dateTo: string;
    branchId?: string;
    costCenterId?: string;
    businessUnitId?: string;
    status?: string;
    includeReversed?: boolean;
    sourceType?: string;
    q?: string;
    take?: number;
    skip?: number;
  }): Promise<any> {
    const response = await api.get('/api/v1/accounting/reports/daily-book', {
      params: {
        ...params,
        includeReversed: params.includeReversed ? 'true' : undefined,
      },
    });
    return response.data;
  }

  async getAccountingReportLedger(params: {
    legalEntityId: string;
    accountId: string;
    dateFrom: string;
    dateTo: string;
    branchId?: string;
    costCenterId?: string;
    businessUnitId?: string;
    includeReversed?: boolean;
    take?: number;
    skip?: number;
  }): Promise<any> {
    const response = await api.get('/api/v1/accounting/reports/ledger', {
      params: {
        ...params,
        includeReversed: params.includeReversed ? 'true' : undefined,
      },
    });
    return response.data;
  }

  async getAccountingEntry(id: string): Promise<any> {
    const response = await api.get(`/api/v1/accounting/entries/${id}`);
    return response.data;
  }

  async getIncomeStatement(params: {
    legalEntityId: string;
    dateFrom: string;
    dateTo: string;
    branchId?: string;
    costCenterId?: string;
    businessUnitId?: string;
    compareFrom?: string;
    compareTo?: string;
    includeReversed?: boolean;
  }): Promise<any> {
    const response = await api.get('/api/v1/accounting/reports/income-statement', {
      params: {
        ...params,
        includeReversed: params.includeReversed ? 'true' : undefined,
      },
    });
    return response.data;
  }

  async getIncomeStatementSegmented(params: {
    legalEntityId: string;
    dateFrom: string;
    dateTo: string;
    segmentBy: 'branch' | 'costCenter' | 'businessUnit';
  }): Promise<any> {
    const response = await api.get('/api/v1/accounting/reports/income-statement/segmented', {
      params,
    });
    return response.data;
  }

  async getBalanceSheet(params: {
    legalEntityId: string;
    asOf: string;
    branchId?: string;
    costCenterId?: string;
    businessUnitId?: string;
    compareAsOf?: string;
    includeReversed?: boolean;
  }): Promise<any> {
    const response = await api.get('/api/v1/accounting/reports/balance-sheet', {
      params: {
        ...params,
        includeReversed: params.includeReversed ? 'true' : undefined,
      },
    });
    return response.data;
  }

  async downloadIncomeStatementPdf(params: {
    legalEntityId: string;
    dateFrom: string;
    dateTo: string;
    branchId?: string;
    costCenterId?: string;
    businessUnitId?: string;
  }): Promise<void> {
    const response = await api.get('/api/v1/accounting/reports/income-statement.pdf', {
      params,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'estado-resultados.pdf';
    a.click();
    URL.revokeObjectURL(url);
  }

  async downloadBalanceSheetPdf(params: {
    legalEntityId: string;
    asOf: string;
    branchId?: string;
    costCenterId?: string;
    businessUnitId?: string;
  }): Promise<void> {
    const response = await api.get('/api/v1/accounting/reports/balance-sheet.pdf', {
      params,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'balance-patrimonial.pdf';
    a.click();
    URL.revokeObjectURL(url);
  }

  async getCashFlowReport(params: {
    legalEntityId: string;
    dateFrom: string;
    dateTo: string;
  }): Promise<any> {
    const response = await api.get('/api/v1/accounting/reports/cash-flow', { params });
    return response.data;
  }

  async getAccountingIntegrity(legalEntityId: string, scope?: string): Promise<any> {
    const response = await api.get('/api/v1/accounting/integrity', {
      params: { legalEntityId, scope },
    });
    return response.data;
  }

  async preCloseAccountingPeriod(periodId: string): Promise<any> {
    const response = await api.post(`/api/v1/accounting/periods/${periodId}/pre-close`);
    return response.data;
  }

  async closeAccountingPeriod(
    periodId: string,
    body?: { acceptWarningCodes?: string[]; notes?: string },
  ): Promise<any> {
    const response = await api.post(`/api/v1/accounting/periods/${periodId}/close`, body ?? {});
    return response.data;
  }

  async reopenAccountingPeriod(periodId: string, reason: string): Promise<any> {
    const response = await api.post(`/api/v1/accounting/periods/${periodId}/reopen`, { reason });
    return response.data;
  }

  async lockAccountingPeriod(
    periodId: string,
    body?: { acceptWarningCodes?: string[]; notes?: string },
  ): Promise<any> {
    const response = await api.post(`/api/v1/accounting/periods/${periodId}/lock`, body ?? {});
    return response.data;
  }

  async getFiscalYears(legalEntityId: string): Promise<any[]> {
    const response = await api.get(`/api/v1/accounting/fiscal-years/${legalEntityId}`);
    return response.data;
  }

  async getGroupConsolidation(params: {
    organizationId?: string;
    legalEntityIds?: string;
  }): Promise<any> {
    const response = await api.get('/api/v1/accounting/group/consolidation', { params });
    return response.data;
  }

  async getCollection(id: string): Promise<Collection> {
    const response = await api.get(`/api/v1/collections/${id}`);
    return response.data;
  }

  async updateCollection(id: string, data: Partial<Collection>): Promise<Collection> {
    const response = await api.patch(`/api/v1/collections/${id}`, data);
    return response.data;
  }

  // KYC
  async getKycChecks(customerId?: string): Promise<any[]> {
    const response = await api.get('/api/v1/kyc', { params: customerId ? { customerId } : {} });
    return response.data;
  }

  async getKycDashboard(): Promise<{ stats: { total: number; pending: number; approved: number; rejected: number }; recentChecks: any[] }> {
    const response = await api.get('/api/v1/kyc/dashboard');
    return response.data;
  }

  async createKycCheck(data: { customerId: string; type: 'IDENTITY' | 'ADDRESS' | 'INCOME' | 'EMPLOYMENT'; checkType: string }): Promise<any> {
    const response = await api.post('/api/v1/kyc', data);
    return response.data;
  }

  async startKycVerification(data: { customerId: string; type: 'IDENTITY' | 'ADDRESS' | 'INCOME' | 'EMPLOYMENT' }): Promise<any> {
    const response = await api.post('/api/v1/kyc/verification', data);
    return response.data;
  }

  async runKycScreening(kycCheckId: string): Promise<{ hit: boolean; hits: { list: string; matchScore: number; details?: string }[]; confidence: number }> {
    const response = await api.post('/api/v1/kyc/screening', { kycCheckId });
    return response.data;
  }

  // Risk
  async getRiskDashboard(): Promise<{
    stats: { total: number; lowRisk: number; mediumRisk: number; highRisk: number };
    recentAssessments: any[];
  }> {
    const response = await api.get('/api/v1/risk/dashboard');
    return response.data;
  }

  async calculateRiskScore(data: {
    customerId: string;
    creditHistory: 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR';
    incomeStability: 'STABLE' | 'MODERATE' | 'UNSTABLE';
    debtToIncome: number;
    employmentYears?: number;
    age?: number;
    creditScore?: number;
    monthlyIncome?: number;
    monthlyExpenses?: number;
  }): Promise<{ score: number; level: 'LOW' | 'MEDIUM' | 'HIGH'; factors: Array<{ name: string; value: any; delta: number }>; assessmentId: string }> {
    const response = await api.post('/api/v1/risk/score', data);
    return response.data;
  }

  async calculateExpectedLoss(data: {
    customerId: string;
    exposureAmount: number;
    collateralValue?: number;
    loanId?: string;
    termMonths?: number;
  }): Promise<{ pd: number; lgd: number; ead: number; expectedLoss: number; score: number; level: string; assessmentId: string }> {
    const response = await api.post('/api/v1/risk/expected-loss', data);
    return response.data;
  }

  // Notifications
  async getNotifications(page = 1, limit = 10): Promise<{ data: Notification[]; total: number; page: number; limit: number }> {
    const response = await api.get('/api/v1/notifications', {
      params: { page, limit }
    });
    return response.data;
  }

  async markNotificationAsRead(id: string): Promise<void> {
    await api.put(`/api/v1/notifications/${id}/read`);
  }

  async markAllNotificationsAsRead(): Promise<void> {
    await api.put('/api/v1/notifications/read-all');
  }

  // Reports — ver también downloadOperationalReportXlsx y downloadReportCsv
}

export const apiService = new ApiService();